// link.component.ts
import { Component, OnInit, AfterContentInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ISlimScrollOptions } from 'ng2-slimscroll';

import { AppService } from '../../app.service';
import { LinkService } from './link.service';
import { TimeCurrencyService, TimeCurrencyInfo } from './../time-currency.service';

import {
  MessageEvent, SignInEvent, LoaderEvent, OpenFrameEvent, SidemenuToViewFacilityEvent,
  SidemenuSubCategory, HiddenFooter
} from './../../shared/message.event';

import { LinkFacility, ActivityCharges } from './link-facility.interface';
import { LinkValidation } from './link-validation';
import { UISETTING } from './../../shared/ui-setting';
import { LinkBase } from './link-base';
import { UtilityFunctions } from '../../shared/utility-functions';
import { CONSTANT_MESSAGES } from '../../constants/constants-messages';
import { CONSTANT_VALUES } from '../../constants/constant-values';

@Component({
  selector: 'app-link',
  templateUrl: './link.component.html',
  styleUrls: ['./link.component.css'],
  providers: [
    LinkService, MessageEvent, SignInEvent, LoaderEvent, OpenFrameEvent,
    SidemenuToViewFacilityEvent, SidemenuSubCategory, HiddenFooter,
    UtilityFunctions, TimeCurrencyService
  ]
})
export class LinkComponent extends LinkBase implements OnInit, AfterContentInit, OnDestroy {
  public externalApiProviders: string[] = CONSTANT_VALUES.EXTERNAL_API_PROVIDERS;

  // Decimal restriction configuration
  pricingConfig = {
    maxValue: 999999,
    decimalPlaces: 2
  };

  /* ================================================================
     Template Element Refs for Toggle Buttons & Modals
  ===================================================================*/
  @ViewChild('memberOff') memberOff: any;
  @ViewChild('memberOn') memberOn: any;
  @ViewChild('nonMemberOff') nonMemberOff: any;
  @ViewChild('nonMemberOn') nonMemberOn: any;
  @ViewChild('guestOff') guestOff: any;
  @ViewChild('guestOn') guestOn: any;
  @ViewChild('corpMemberOff') corpMemberOff: any;
  @ViewChild('corpMemberOn') corpMemberOn: any;
  @ViewChild('corpNonMemberOff') corpNonMemberOff: any;
  @ViewChild('corpNonMemberOn') corpNonMemberOn: any;
  @ViewChild('memberChildOff') memberChildOff: any;
  @ViewChild('memberChildOn') memberChildOn: any;
  @ViewChild('nonMemberChildOff') nonMemberChildOff: any;
  @ViewChild('nonMemberChildOn') nonMemberChildOn: any;
  @ViewChild('guestChildOff') guestChildOff: any;
  @ViewChild('guestChildOn') guestChildOn: any;

  // Generic toggles (CTA, GST etc)
  @ViewChild('butt17') butt17: any;
  @ViewChild('butt18') butt18: any;
  @ViewChild('butt27') butt27: any;
  @ViewChild('butt28') butt28: any;
  @ViewChild('popUpObj') popUpObj: any;
  @ViewChild('confirmationForDataLosspopUpObj') confirmationForDataLosspopUpObj: any;
  @ViewChild('HomeSceenpopUpObj') HomeSceenpopUpObj: any;
  @ViewChild('activityPopUpObj') activityPopUpObj: any;
  @ViewChild('alertImagePopUpObj') alertImagePopUpObj: any;

  /* ================================================================
     External API fields
  ===================================================================*/
  public isTestingConnection: boolean = false;
  public testConnectionResult: string = '';
  public testConnectionError: string = '';

  public isTestingDigirestroConnection: boolean = false;
  public digirestroTestResult: string = '';
  public digirestroTestError: string = '';

  protected groupList: any[] = [];
  public isopenGroups: boolean = false;
  public currentCategory: any = null;
  /* ================================================================
     Template & UI State
  ===================================================================*/
  protected uiSetting: UISETTING;
  protected height: any;
  protected scrollOptions: ISlimScrollOptions;
  protected popupTitle = 'Error';
  protected popUpMessage: any = '';
  protected cancleButton = false;

  // Validation
  protected linkValidationObj: LinkValidation;

  // Currency
  protected currencyInfo: TimeCurrencyInfo;
  protected selectedGlobalCurrency = '';
  protected currentCurrencyDisplay = '';

  // Facility (Link)
  public facility: LinkFacility;
  protected facility_list: LinkFacility[] = [];
  protected viewIcon = { name: '', iconClass: '' };

  protected maxDate: Date = new Date();
  protected yearRange: string = "2024:2030";
  protected minDate: Date = new Date();

  protected linkTypeOptions = [
    'Hyperlink',
    'Google Form',
    'Fee Payment',
    'External API',
    'Others'
  ];

  // Flags / states
  public addLinkStatus = true;
  public pageName = 'Create Link';
  public viewStatus = false;
  public editStatus = false;
  public isReadOnly = false;
  private componentDestroyed = false;

  // CTA toggle mirror
  public isGSTApplicable: boolean = false;

  public showEventPricing: boolean = false;
  public showExternalApiConfig: boolean = false;

  // helper list for pricing link types
  private pricingLinkTypes: string[] = ['fee payment', 'fee_payment', 'feepayment'];

  public iconImage: any[] = [];   // fill this with your available icons

  // ---------- Added missing properties referenced in the file ----------
  public vendor: any = null;
  public clubName: string = '';
  public shouldSlide: boolean = false;
  public deleteMessage: boolean = false;
  public cancleFlag: boolean = false;
  public saveFlag: boolean = false;
  public tempFacilityId: string = '';
  public openTime_hour: string = '01';
  public openTime_minute: string = '00';
  public closeTime_hour: string = '23';
  public closeTime_minute: string = '00';

  // quick mirrors that some methods toggle
  public isAttendeeCountRequired: boolean = false;
  public isWaitlistEnabled: boolean = false;
  public calendarExpiryDate: Date | null = null;

  constructor(
    private router: Router,
    private linkService: LinkService,
    private openFrameEvent: OpenFrameEvent,
    private messageEvent: MessageEvent,
    private signInEvent: SignInEvent,
    private appService: AppService,
    private loaderEvent: LoaderEvent,
    private sidemenuSubCategory: SidemenuSubCategory,
    private sidemenuToViewFacilityEvent: SidemenuToViewFacilityEvent,
    private hiddenFooter: HiddenFooter,
    private utilityFunctions: UtilityFunctions,
    private timeCurrencyService: TimeCurrencyService
  ) {
    super();

    this.uiSetting = {
      windowHeight: window.innerHeight,
      topNav: 56,
      midNav: 45,
      container: 80,
      footer: 35
    };

    // create linkValidationObj instance (defensive)
    try {
      this.linkValidationObj = new LinkValidation(
        this.appService,
        this.utilityFunctions,
        this.timeCurrencyService
      );
    } catch (e) {
      // In case DI ordering or something prevents it, leave null and handle later
      console.warn('LinkValidation instantiation failed:', e);
      this.linkValidationObj = null as any;
    }

    this.resetLocalState();

    this.scrollOptions = {
      barBackground: '#343a40',
      barOpacity: '0.6',
      position: 'right',
      barWidth: '6'
    };

    this.height = (
      this.uiSetting.windowHeight -
      (this.uiSetting.topNav + this.uiSetting.midNav + this.uiSetting.container + this.uiSetting.footer)
    ) + 'px';
  }

  ngOnInit(): void {
    this.emitOpenFrameEvent();
    this.pageName = 'Create Link';

    // Load vendor (defensive)
    if (localStorage.getItem('vendor')) {
      try {
        this.vendor = JSON.parse(atob(localStorage.getItem('vendor')));
        if (this.vendor && this.vendor.role === CONSTANT_VALUES.SUPER_ADMIN) {
          if (this.vendor.subVendors && this.vendor.subVendors.length > 0) {
            const subVendor = this.vendor.subVendors[0];
            this.clubName = subVendor && subVendor.clubName ? subVendor.clubName : '';
          }
        } else {
          this.clubName = (this.vendor && this.vendor.clubName) ? this.vendor.clubName : '';
        }
      } catch (e) {
        console.warn('Failed to parse vendor from localStorage', e);
        this.vendor = {};
      }
    }

    this.iconImage = JSON.parse(JSON.stringify(CONSTANT_VALUES.ICON_LIST_IMAGE || []));

    this.setFrameForFacilityRules();
    this.emitTypeBroadcast();
    this.registerTypeBroadcast();
    // initialize decimals if facility was loaded
    this.initializePricingWithDecimals();

    // expiry date range
    this.minDate = new Date();
    this.minDate.setHours(0, 0, 0, 0);
    this.maxDate = new Date();
    this.maxDate.setFullYear(this.maxDate.getFullYear() + 2);

    const currentYear = new Date().getFullYear();
    this.yearRange = currentYear + ':' + (currentYear + 2);

    this.updateCurrencyInfo();

    // default today's date for new links
    if (this.addLinkStatus && !this.editStatus && !this.viewStatus) {
      if (!this.facility) this.resetLocalState();
      this.facility.dateObj = new Date();
      this.facility.date = this.formatDateTimeWithZeroTime(this.facility.dateObj);
    }

    // Initialize external API structure with Digirestro defaults from constants
    if (!this.facility.externalApi) {
      this.facility.externalApi = {
        enabled: false,
        provider: '',
        shopify: {
          shopName: '',
          apiKey: '',
          password: '',
          accessToken: ''
        },
        digirestro: {
          baseUrl: CONSTANT_VALUES.EXTERNAL_PROVIDER_API_SCHEMA.digirestro.baseUrl,
          apiKey: CONSTANT_VALUES.EXTERNAL_PROVIDER_API_SCHEMA.digirestro.apiKey,
          storeId: CONSTANT_VALUES.EXTERNAL_PROVIDER_API_SCHEMA.digirestro.storeId,
          browserUrl: CONSTANT_VALUES.EXTERNAL_PROVIDER_API_SCHEMA.digirestro.browserUrl,
          integrationId: CONSTANT_VALUES.EXTERNAL_PROVIDER_API_SCHEMA.digirestro.integrationId
        },
        custom: {
          url: '',
          headers: ''
        }
      };
    } else {
      // Ensure nested objects exist
      if (!this.facility.externalApi.shopify) {
        this.facility.externalApi.shopify = {
          shopName: '',
          apiKey: '',
          password: '',
          accessToken: ''
        };
      }

      if (!this.facility.externalApi.digirestro) {
        this.facility.externalApi.digirestro = {
          baseUrl: CONSTANT_VALUES.EXTERNAL_PROVIDER_API_SCHEMA.digirestro.baseUrl,
          apiKey: CONSTANT_VALUES.EXTERNAL_PROVIDER_API_SCHEMA.digirestro.apiKey,
          storeId: CONSTANT_VALUES.EXTERNAL_PROVIDER_API_SCHEMA.digirestro.storeId,
          browserUrl: CONSTANT_VALUES.EXTERNAL_PROVIDER_API_SCHEMA.digirestro.browserUrl,
          integrationId: CONSTANT_VALUES.EXTERNAL_PROVIDER_API_SCHEMA.digirestro.integrationId
        };
      }

      if (!this.facility.externalApi.custom) {
        this.facility.externalApi.custom = {
          url: '',
          headers: ''
        };
      }
    }
  }

  ngAfterContentInit() { }
  ngOnDestroy() { this.componentDestroyed = true; }

  /* ================================================================
     Utility helpers
  ===================================================================*/

  /**
 * Parse many common date formats into a JS Date or null.
 * Returns a Date (valid) or null.
 */
  private parseToDateOrNull(raw: any): Date | null {
    if (raw === null || typeof raw === 'undefined' || raw === '') return null;

    // if already a Date
    if (raw instanceof Date) {
      return isNaN(raw.getTime()) ? null : raw;
    }

    const s = ('' + raw).trim();
    if (!s) return null;

    // 1) Try native Date parse (handles ISO strings usually)
    let d = new Date(s);
    if (!isNaN(d.getTime())) return d;

    // 2) Replace first space with 'T' to handle "YYYY-MM-DD HH:MM:SS(.sss)"
    d = new Date(s.replace(' ', 'T'));
    if (!isNaN(d.getTime())) return d;

    // 3) YYYY-MM-DD or YYYY/MM/DD
    let m = s.match(/^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})$/);
    if (m) {
      const y = +m[1], mo = +m[2] - 1, da = +m[3];
      const dd = new Date(y, mo, da);
      return isNaN(dd.getTime()) ? null : dd;
    }

    // 4) DD-MM-YYYY or DD/MM/YYYY
    m = s.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
    if (m) {
      const da = +m[1], mo = +m[2] - 1, y = +m[3];
      const dd = new Date(y, mo, da);
      return isNaN(dd.getTime()) ? null : dd;
    }

    // 5) Datetime like "YYYY-MM-DD HH:MM:SS(.sss)"
    m = s.match(/^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2})(?:\.(\d{1,3}))?)?$/);
    if (m) {
      const y = +m[1], mo = +m[2] - 1, da = +m[3],
        hh = +m[4], mm = +m[5], ss = m[6] ? +m[6] : 0, ms = m[7] ? +m[7] : 0;
      const dd = new Date(y, mo, da, hh, mm, ss, ms);
      return isNaN(dd.getTime()) ? null : dd;
    }

    // Not parseable
    return null;
  }

  private toDateOrNull(val: any): Date | null {
    if (!val) return null;
    if (val instanceof Date && !isNaN(val.getTime())) return val;
    try {
      const d = new Date(val);
      if (!isNaN(d.getTime())) return d;
    } catch (e) { }
    return null;
  }
  private emitOpenFrameEvent() { try { this.openFrameEvent.fire(false); } catch (e) { /* ignore */ } }

  private emitTypeBroadcast() {
    const isShow: any = { privacypolicy: true, support: false, footer: 'block' };
    try { this.signInEvent.fire('block'); } catch (e) { /* ignore */ }
    try { this.hiddenFooter.fire(isShow); } catch (e) { /* ignore */ }
  }

  private registerTypeBroadcast() {
    try {
      this.messageEvent.on().subscribe((message: any) => {
        this.shouldSlide = message;
      });
    } catch (e) { /* ignore */ }

    try {
      this.sidemenuToViewFacilityEvent.on().subscribe((message: any) => {
        this.currentCategory = message;
        try { localStorage.setItem('currentCategory', btoa(JSON.stringify(message))); } catch (e) { /* ignore */ }
        this.pageName = 'Create Link';
        try { this.vendor = JSON.parse(atob(localStorage.getItem('vendor'))); } catch (e) { /* ignore */ }
        this.addLinkStatus = false;
        this.viewFacility('category');
      });
    } catch (e) { /* ignore */ }
  }

  private formatFullDateTime(input: any): string {
    if (!input) return '';

    let date: Date;
    if (input instanceof Date) {
      date = input;
    } else {
      date = new Date(input);
    }

    if (isNaN(date.getTime())) return '';

    const pad = (n: number, width: number = 2) => {
      const s = n.toString();
      return s.length >= width ? s : new Array(width - s.length + 1).join('0') + s;
    };

    return (
      date.getFullYear() +
      '-' + pad(date.getMonth() + 1) +
      '-' + pad(date.getDate()) +
      ' ' + pad(date.getHours()) +
      ':' + pad(date.getMinutes()) +
      ':' + pad(date.getSeconds()) +
      '.' + pad(date.getMilliseconds(), 3)
    );
  }

  private formatDateTimeWithZeroTime(input: any): string {
    if (!input) return '';

    let date: Date;
    if (input instanceof Date) {
      date = input;
    } else {
      date = new Date(input);
    }

    if (isNaN(date.getTime())) return '';

    // normalize to midnight
    date.setHours(0, 0, 0, 0);

    const pad = (n: number, width: number = 2) => {
      const s = n.toString();
      return s.length >= width ? s : new Array(width - s.length + 1).join('0') + s;
    };

    return (
      date.getFullYear() +
      '-' + pad(date.getMonth() + 1) +
      '-' + pad(date.getDate()) +
      ' ' + pad(date.getHours()) +
      ':' + pad(date.getMinutes()) +
      ':' + pad(date.getSeconds()) +
      '.' + pad(date.getMilliseconds(), 3)
    );
  }


  /* ================================================================
     Pricing & currency helpers
  ===================================================================*/
  public formatPriceDisplay(value: any): string {
    if (!value && value !== 0) {
      return '';
    }
    const numValue = parseFloat(value || '0');
    if (isNaN(numValue)) return '';
    return numValue.toFixed(2);
  }

  private initializePricingWithDecimals(): void {
    if (!this.facility || !this.facility.activityCharges) return;

    const tiers = ['member', 'nonMember', 'guest', 'corpMember', 'corpNonMember'];
    const childPricingTiers = ['member', 'nonMember', 'guest'];

    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];
      const pricingTier = (this.facility.activityCharges as any)[tier];
      if (pricingTier) {
        try {
          if (pricingTier.amount && pricingTier.amount !== '') {
            pricingTier.amount = this.formatPriceDisplay(pricingTier.amount);
          }
          if (childPricingTiers.indexOf(tier) !== -1) {
            if (pricingTier.childEnabled && pricingTier.childAmount && pricingTier.childAmount !== '') {
              pricingTier.childAmount = this.formatPriceDisplay(pricingTier.childAmount);
            }
          }
        } catch (error) {
          console.warn('Error formatting pricing for tier:', tier, error);
        }
      }
    }

    try {
      if (this.facility.GSTValue && this.facility.GSTValue !== '') {
        this.facility.GSTValue = this.formatPriceDisplay(this.facility.GSTValue);
      }
    } catch (error) {
      console.warn('Error formatting GST value:', error);
    }
  }

  private updateCurrencyInfo(): void {
    const vendorCountry = (this.vendor && this.vendor.country) ? this.vendor.country : 'Singapore';
    this.currencyInfo = this.timeCurrencyService.getTimeCurrencyInfo(vendorCountry);
    this.updateCurrencyDisplay();
  }

  private updateCurrencyDisplay(): void {
    if (this.currencyInfo) {
      this.currentCurrencyDisplay = this.currencyInfo.symbol + ' ' + this.currencyInfo.currency + ' - ' + this.currencyInfo.name;
    } else {
      this.currentCurrencyDisplay = 'S$ SGD - Singapore Dollar';
    }
  }

  public getCurrency(): string {
    return this.currencyInfo ? this.currencyInfo.currency : 'SGD';
  }

  public getCurrencySymbol(): string {
    return this.currencyInfo ? this.currencyInfo.symbol : 'S$';
  }

  public getCurrentCurrencyDisplay(): string {
    return this.currentCurrencyDisplay;
  }

  public getExtendedMajorCurrencies(): TimeCurrencyInfo[] {
    return this.timeCurrencyService.getMajorCurrencies();
  }

  private getCurrencyInfoByCurrencyCode(currencyCode: string): TimeCurrencyInfo {
    const allCurrencies = this.timeCurrencyService.getAllCurrencies();
    for (let i = 0; i < allCurrencies.length; i++) {
      if (allCurrencies[i].currency === currencyCode) {
        return allCurrencies[i];
      }
    }
    return this.timeCurrencyService.getTimeCurrencyInfo('Singapore');
  }

  public onGlobalCurrencyChange(selectedCurrency: string): void {
    if (!this.facility || !this.facility.activityCharges) return;

    let currencyInfo: TimeCurrencyInfo;
    if (selectedCurrency && selectedCurrency !== '') {
      currencyInfo = this.getCurrencyInfoByCurrencyCode(selectedCurrency);
    } else {
      currencyInfo = this.timeCurrencyService.getTimeCurrencyInfo(this.vendor ? this.vendor.country : 'Singapore');
    }
    if (!currencyInfo) return;

    const categories = ['member', 'nonMember', 'guest', 'corpMember', 'corpNonMember'];
    for (let i = 0; i < categories.length; i++) {
      const category = categories[i];
      if (this.facility.activityCharges && (this.facility.activityCharges as any)[category]) {
        const pTier = (this.facility.activityCharges as any)[category];
        pTier.currency = currencyInfo.currency;
        pTier.currencyCode = currencyInfo.code;
        pTier.currencySymbol = currencyInfo.symbol;
        pTier.currencyName = currencyInfo.name;
        pTier.minorUnit = currencyInfo.minorUnit;
      }
    }
    if (this.facility.amount) {
      this.facility.amount.currency = currencyInfo.currency;
    }
    this.currencyInfo = currencyInfo;
    this.updateCurrencyDisplay();
  }

  /* ================================================================
     Pricing toggles and helpers (unchanged logic)
  ===================================================================*/
  public togglePricing(that: any, tier: string, enable: boolean): void {
    if (!this.facility) return;
    if (!this.facility.activityCharges) this.facility.activityCharges = {} as ActivityCharges;

    if (!this.facility.activityCharges[tier]) {
      const currencyInfo = this.timeCurrencyService.getTimeCurrencyInfo(this.vendor ? this.vendor.country : 'Singapore');
      const initTier: any = {
        enabled: false,
        amount: '',
        currency: currencyInfo.currency,
        currencyCode: currencyInfo.code,
        currencySymbol: currencyInfo.symbol,
        currencyName: currencyInfo.name,
        minorUnit: currencyInfo.minorUnit,
        country: currencyInfo.country
      };
      if (tier === 'member' || tier === 'nonMember' || tier === 'guest') {
        initTier.childEnabled = false;
        initTier.childAmount = '';
      }
      (this.facility.activityCharges as any)[tier] = initTier;
    }

    const pricingTier = (this.facility.activityCharges as any)[tier] as any;
    pricingTier.enabled = enable;

    if (!enable) {
      pricingTier.amount = '';
      if (pricingTier.childEnabled !== undefined) {
        pricingTier.childEnabled = false;
        pricingTier.childAmount = '';
      }
    } else {
      if (!pricingTier.amount || pricingTier.amount === '') pricingTier.amount = '0.00';
      if (tier === 'member' || tier === 'nonMember' || tier === 'guest') {
        pricingTier.childEnabled = false;
        pricingTier.childAmount = '';
      }
    }

    this.setToggleState(tier, enable);
    if (tier === 'member' || tier === 'nonMember' || tier === 'guest') {
      this.setChildToggleState(tier, false);
    }
  }

  public toggleChildPricing(that: any, tier: string, enable: boolean): void {
    if (!this.facility || !this.facility.activityCharges || !this.facility.activityCharges[tier]) return;

    const pricingTier = (this.facility.activityCharges as any)[tier] as any;
    const allowed = ['member', 'nonMember', 'guest'];
    if (allowed.indexOf(tier) === -1) {
      return;
    }

    if (!pricingTier.enabled) {
      pricingTier.childEnabled = false;
      pricingTier.childAmount = '';
      this.setChildToggleState(tier, false);
      return;
    }

    pricingTier.childEnabled = enable;
    if (!enable) pricingTier.childAmount = '';
    else if (!pricingTier.childAmount || pricingTier.childAmount === '') pricingTier.childAmount = '0.00';

    this.setChildToggleState(tier, enable);
  }

  public onPricingAmountChange(tier: string, amount: any): void {
    if (!this.facility || !this.facility.activityCharges || !this.facility.activityCharges[tier]) return;
    const pricingTier = (this.facility.activityCharges as any)[tier] as any;
    let numericAmount = parseFloat(amount || '0');

    if (isNaN(numericAmount)) numericAmount = 0;

    if (numericAmount === 0 && pricingTier.enabled) {
      pricingTier.enabled = false;
      pricingTier.amount = '';
      if (pricingTier.childEnabled !== undefined) {
        pricingTier.childEnabled = false;
        pricingTier.childAmount = '';
      }
      this.setToggleState(tier, false);
      if (tier === 'member' || tier === 'nonMember' || tier === 'guest') {
        this.setChildToggleState(tier, false);
      }
    }
  }

  private onChildPricingAmountChange(tier: string, childAmount: any): void {
    if (!this.facility || !this.facility.activityCharges || !this.facility.activityCharges[tier]) return;
    const pricingTier = (this.facility.activityCharges as any)[tier] as any;
    let numericAmount = parseFloat(childAmount || '0');
    if (isNaN(numericAmount)) numericAmount = 0;

    if (numericAmount === 0 && pricingTier.childEnabled) {
      pricingTier.childEnabled = false;
      pricingTier.childAmount = '';
      this.setChildToggleState(tier, false);
    }
  }

  private setToggleState(tier: string, enabled: boolean): void {
    const onBtn = (this as any)[tier + 'On'];
    const offBtn = (this as any)[tier + 'Off'];
    if (!onBtn || !offBtn) return;

    try {
      const onEl = onBtn.nativeElement ? onBtn.nativeElement : onBtn;
      const offEl = offBtn.nativeElement ? offBtn.nativeElement : offBtn;
      if (enabled) {
        onEl.classList.add('btn-toggle1');
        onEl.classList.remove('btn-toggle2');
        offEl.classList.add('btn-toggle2');
        offEl.classList.remove('btn-toggle1');
      } else {
        offEl.classList.add('btn-toggle1');
        offEl.classList.remove('btn-toggle2');
        onEl.classList.add('btn-toggle2');
        onEl.classList.remove('btn-toggle1');
      }
    } catch (e) {
      // ignore DOM errors
    }
  }

  private setChildToggleState(tier: string, enabled: boolean): void {
    const onBtn = (this as any)[tier + 'ChildOn'];
    const offBtn = (this as any)[tier + 'ChildOff'];
    if (!onBtn || !offBtn) return;

    try {
      const onEl = onBtn.nativeElement ? onBtn.nativeElement : onBtn;
      const offEl = offBtn.nativeElement ? offBtn.nativeElement : offBtn;
      if (enabled) {
        onEl.classList.add('btn-toggle1');
        onEl.classList.remove('btn-toggle2');
        offEl.classList.add('btn-toggle2');
        offEl.classList.remove('btn-toggle1');
      } else {
        offEl.classList.add('btn-toggle1');
        offEl.classList.remove('btn-toggle2');
        onEl.classList.add('btn-toggle2');
        onEl.classList.remove('btn-toggle1');
      }
    } catch (e) {
      // ignore
    }
  }

  /* ================================================================
     Small helpers & validation wiring
  ===================================================================*/
  public validateField(fieldName: string, value: any): boolean {
    if (this.linkValidationObj && typeof this.linkValidationObj.validateFacility === 'function') {
      try {
        return this.linkValidationObj.validateFacility(this, fieldName, value);
      } catch (err) {
        console.warn('validateField delegation failed', err);
        return true;
      }
    }
    return true;
  }

  private normalizeLinkType(linkType: any): string {
    if (!linkType && this.facility && this.facility.linkType) {
      linkType = this.facility.linkType;
    }
    if (!linkType) return '';
    return ('' + linkType).toString().trim().toLowerCase();
  }

  public updateShowEventPricingForLinkType(linkType?: any): void {
    const normalized = this.normalizeLinkType(linkType);
    this.showEventPricing = this.pricingLinkTypes.indexOf(normalized) !== -1;
    if (this.facility) this.facility.isChargable = this.showEventPricing;
  }

  public selectIcon(iconObj: any): void {
    if (!this.facility) return;
    this.facility.icon = iconObj && iconObj.name ? iconObj.name : '';
    this.viewIcon = {
      name: iconObj && iconObj.name ? iconObj.name : '',
      iconClass: iconObj && iconObj.iconClass ? iconObj.iconClass : ''
    };

    try {
      if (this.linkValidationObj && typeof this.linkValidationObj.validateFacility === 'function') {
        this.linkValidationObj.validateFacility(this, 'icon', this.facility.icon);
      }
    } catch (e) {
      console.warn('selectIcon: validation call failed', e);
    }
  }

  public onExternalProviderChange(): void {
    this.testConnectionResult = '';
    this.testConnectionError = '';
    this.isTestingConnection = false;

    if (!this.facility) return;
    if (!this.facility['externalApi']) {
      this.facility['externalApi'] = { enabled: false, provider: '', shopify: {}, custom: {} } as any;
    } else {
      if (!this.facility['externalApi'].shopify) this.facility['externalApi'].shopify = {};
      if (!this.facility['externalApi'].custom) this.facility['externalApi'].custom = {};
    }
  }

  public resetFormForNewLinkType(): void {
    if (!this.facility) return;
    if (this.facility.activityCharges) {
      const tiers = ['member', 'nonMember', 'guest', 'corpMember', 'corpNonMember'];
      for (let i = 0; i < tiers.length; i++) {
        const t = tiers[i];
        const pr = (this.facility.activityCharges as any)[t];
        if (pr) {
          pr.enabled = false;
          pr.amount = '';
          if (pr.childEnabled !== undefined) {
            pr.childEnabled = false;
            pr.childAmount = '';
          }
        }
      }
    }

    this.facility.isGSTApplicable = false;
    this.facility.GSTValue = '0.00';
    this.facility.isChargable = false;

    if (!this.facility['externalApi']) {
      this.facility['externalApi'] = { enabled: false, provider: '', shopify: {}, custom: {} } as any;
    } else {
      this.facility['externalApi'].enabled = false;
      this.facility['externalApi'].provider = '';
      this.facility['externalApi'].shopify = this.facility['externalApi'].shopify || {};
      this.facility['externalApi'].shopify.shopName = '';
      this.facility['externalApi'].shopify.apiKey = '';
      this.facility['externalApi'].shopify.password = '';
      this.facility['externalApi'].shopify.accessToken = '';
      this.facility['externalApi'].custom = this.facility['externalApi'].custom || {};
      this.facility['externalApi'].custom.url = '';
      this.facility['externalApi'].custom.headers = '';
    }

    this.facility.ctaEnabled = false;
    this.facility.ctaText = '';
    this.facility.ctaLink = '';

    this.isGSTApplicable = false;
    this.testConnectionResult = '';
    this.testConnectionError = '';
    this.isTestingConnection = false;

    try { this.initializeAllTogglesToOff(); } catch (e) { /* ignore */ }
  }

  public updateToggleUI(tier: string, enabled: boolean): void {
    try { this.setToggleState(tier, !!enabled); } catch (e) { console.warn('updateToggleUI failed', e); }
  }

  public updateChildToggleUI(tier: string, enabled: boolean): void {
    try { this.setChildToggleState(tier, !!enabled); } catch (e) { console.warn('updateChildToggleUI failed', e); }
  }

  public generateFullLocation(): string {
    if (!this.facility) return '';
    const parts: string[] = [];
    if (this.facility.addressLine1) parts.push((this.facility.addressLine1 || '').trim());
    if (this.facility.addressLine2) parts.push((this.facility.addressLine2 || '').trim());
    if (this.facility.addressLine3) parts.push((this.facility.addressLine3 || '').trim());
    if (this.facility.pincode) parts.push((this.facility.pincode || '').trim());
    if (this.facility.country) parts.push((this.facility.country || '').trim());
    this.facility.location = parts.join(', ');
    return this.facility.location;
  }

  public applyLinkFieldsToFacility(): void {
    if (!this.facility) return;
    if (!this.facility.slotDuration) this.facility.slotDuration = '0';
    if (typeof this.facility.ctaEnabled === 'undefined') this.facility.ctaEnabled = false;
    if (!this.facility.timings) this.facility.timings = { openTime: '01:00', closeTime: '23:00' };
  }

  public initializeDateForCalendar(): void {
    try {
      if (this.facility && this.facility.date && !this.facility.dateObj) {
        this.facility.dateObj = this.facility.date ? new Date(this.facility.date) : new Date();
      }

      if (this.facility) {
        // Normalize expiry to a Date object (or null)
        try {
          const raw = this.facility.expiryDateObj || this.facility.expiryDate || null;
          const parsed = this.parseToDateOrNull(raw);
          if (parsed) {
            parsed.setHours(0, 0, 0, 0);
            this.facility.expiryDateObj = parsed;
            // keep canonical string format if you use it
            try { this.facility.expiryDate = this.formatDateTimeWithZeroTime(parsed); } catch (e) { /* ignore */ }
          } else {
            this.facility.expiryDateObj = null;
          }
        } catch (e) {
          this.facility.expiryDateObj = null;
        }

        // keep calendar model in sync (if you use it)
        try {
          this.calendarExpiryDate = this.facility.expiryDateObj ? new Date(this.facility.expiryDateObj) : null;
        } catch (e) {
          this.calendarExpiryDate = null;
        }
      }
    } catch (e) { /* ignore */ }
  }


  /* ================================================================
     Link-specific helpers & toggles
  ===================================================================*/
  public onLinkTypeChange(nextType: any): void {
    if (!this.facility) return;

    const linkTypeNormalized = (nextType || '').toString().trim().toLowerCase();
    const isFeePayment = (linkTypeNormalized === 'fee payment' || linkTypeNormalized === 'feepayment');
    const isExternalApi = (linkTypeNormalized === 'external api' || linkTypeNormalized === 'externalapi' || linkTypeNormalized === 'external');

    this.facility.isChargable = isFeePayment;
    this.showEventPricing = isFeePayment;
    this.showExternalApiConfig = isExternalApi;

    if (!isFeePayment) {
      if (this.facility.activityCharges) {
        const tiers = ['member', 'nonMember', 'guest', 'corpMember', 'corpNonMember'];
        for (let i = 0; i < tiers.length; i++) {
          const t = tiers[i];
          const pr = (this.facility.activityCharges as any)[t];
          if (pr) {
            pr.enabled = false;
            pr.amount = '';
            if (pr.childEnabled !== undefined) {
              pr.childEnabled = false;
              pr.childAmount = '';
            }
          }
        }
      }

      if (this.facility.amount) this.facility.amount.value = '';
      this.facility.isGSTApplicable = false;
      this.facility.GSTValue = '0.00';

      try { this.initializeAllTogglesToOff(); } catch (e) { console.warn('initializeAllTogglesToOff failed', e); }
    }

    if (!isExternalApi) {
      if (this.facility['externalApi']) {
        this.facility['externalApi'].enabled = false;
        if (this.facility['externalApi'].shopify) {
          this.facility['externalApi'].shopify.shopName = '';
          this.facility['externalApi'].shopify.apiKey = '';
          this.facility['externalApi'].shopify.password = '';
          this.facility['externalApi'].shopify.accessToken = '';
        }
      }
      this.testConnectionResult = '';
      this.testConnectionError = '';
      this.isTestingConnection = false;
    }

    if (this.linkValidationObj && this.linkValidationObj.validateFacility) {
      try {
        this.linkValidationObj.validateFacility(this, 'facility.linkType', nextType);
      } catch (err) { console.warn('validateFacility call failed', err); }
    }
  }

  private initializeAllTogglesToOff(): void {
    setTimeout(() => {
      try {
        const pricingTiers = ['member', 'nonMember', 'guest', 'corpMember', 'corpNonMember'];
        for (let i = 0; i < pricingTiers.length; i++) {
          const t = pricingTiers[i];
          this.setToggleState(t, false);
          this.setChildToggleState(t, false);
        }
        if (this.butt18 && this.butt17) {
          try {
            const bOn = this.butt17.nativeElement ? this.butt17.nativeElement : this.butt17;
            const bOff = this.butt18.nativeElement ? this.butt18.nativeElement : this.butt18;
            bOff.classList.add('btn-toggle1');
            bOff.classList.remove('btn-toggle2');
            bOn.classList.add('btn-toggle2');
            bOn.classList.remove('btn-toggle1');
          } catch (err) { /* ignore */ }
        }
      } catch (err) { /* ignore */ }
    }, 400);
  }

  public toggleCTA(arg: any, enable: boolean): void {
    try { if (arg && arg.preventDefault) arg.preventDefault(); } catch (e) { }

    if (!this.facility) this.facility = {} as any;
    this.facility.ctaEnabled = !!enable;

    if (!enable) {
      this.facility.ctaText = '';
      this.facility.ctaLink = '';
      try {
        if (this.linkValidationObj && typeof this.linkValidationObj.validateFacility === 'function') {
          this.linkValidationObj.validateFacility(this, 'facility.ctaText', this.facility.ctaText);
          this.linkValidationObj.validateFacility(this, 'facility.ctaLink', this.facility.ctaLink);
        }
      } catch (e) { console.warn('CTA validation update failed', e); }
    }

    if (typeof (this as any).isCtaEnabled !== 'undefined') {
      (this as any).isCtaEnabled = !!enable;
    }
  }

  public toggle(component: any, toggleNumber: number, value: boolean): void {
    const cmp: any = (component && component.facility) ? component : this;
    if (!cmp || !cmp.facility) return;

    switch (toggleNumber) {
      case 1:
        cmp.facility.isAttendeeCountRequired = !!value;
        cmp.isAttendeeCountRequired = !!value;
        break;
      case 2:
        cmp.facility.isWaitlistEnabled = !!value;
        cmp.isWaitlistEnabled = !!value;
        break;
      case 10:
        cmp.facility.isAllowForceBooking = !!value;
        break;
      case 15:
        cmp.facility.isCreatedForGroup = !!value;
        // If enabling group targeting, load groups for vendor
        if (value) {
          // load group list (the method you shared)
          cmp.getGroupListByVendor();
        } else {
          // clear selected groups when disabling
          if (cmp.facility) {
            cmp.facility.targetGroupIds = [];
          }
          cmp.groupList = [];
        }
        break;
      case 17:
        cmp.facility.isGSTApplicable = !!value;
        cmp.isGSTApplicable = !!value;
        break;
      default:
        console.warn('Unhandled toggle code:', toggleNumber, value);
        break;
    }

    try {
      if (this.linkValidationObj && typeof this.linkValidationObj.validateFacility === 'function') {
        if (toggleNumber === 17) {
          this.linkValidationObj.validateFacility(cmp, 'facility.GSTValue', cmp.facility.GSTValue);
        } else if (toggleNumber === 2) {
          this.linkValidationObj.validateFacility(cmp, 'facility.waitlistCount', cmp.facility.waitlistCount);
        } else if (toggleNumber === 1) {
          this.linkValidationObj.validateFacility(cmp, 'facility.maxNumberOfPeople', cmp.facility.maxNumberOfPeople);
        }
      }
    } catch (e) { console.warn('Validation call after toggle failed', e); }
  }

  /* ================================================================
     External API helpers
  ===================================================================*/
  public isExternalApiLink(fac?: any): boolean {
    let linkType = '';
    if (fac && fac.linkType) {
      linkType = ('' + fac.linkType).toString().trim().toLowerCase();
    } else if (this.facility && this.facility.linkType) {
      linkType = ('' + this.facility.linkType).toString().trim().toLowerCase();
    }
    return linkType === 'external api' || linkType === 'externalapi' || linkType === 'external';
  }

  public toggleExternalApiEnable(enable: boolean): void {
    if (!this.facility) return;
    if (!this.facility['externalApi']) this.facility['externalApi'] = { enabled: false, provider: '', shopify: {}, custom: {} } as any;
    this.facility['externalApi'].enabled = enable;
    if (!enable) {
      this.testConnectionResult = '';
      this.testConnectionError = '';
      this.isTestingConnection = false;
    }
  }

  private validateExternalApiConfig(popUpObj: any): boolean {
    if (!this.facility || !this.facility['externalApi']) return true;
    const ext = this.facility['externalApi'];
    // Digirestro Store ID validation
    if (ext.provider && ext.provider.toLowerCase() === 'digirestro') {
      const digi = ext.digirestro || {};
      if (digi.storeId === null || digi.storeId === undefined) {
        this.popUpMessage = 'Digirestro Store ID must be selected.';
        this.popupTitle = 'Digirestro Configuration';
        if (popUpObj && popUpObj.showChildModal) popUpObj.showChildModal();
        return false;
      }
    }
    if (!ext.enabled) return true;

    if (!ext.provider || ext.provider.toString().trim() === '') {
      this.popUpMessage = 'Please select an External API provider.';
      this.popupTitle = 'External API Configuration';
      if (popUpObj && popUpObj.showChildModal) popUpObj.showChildModal();
      return false;
    }

    const provider = (ext.provider || '').toString().trim().toLowerCase();
    if (provider === 'shopify') {
      const shop = ext.shopify || {};
      if (!shop.shopName || shop.shopName.toString().trim() === '') {
        this.popUpMessage = 'Shopify shop name is required.';
        this.popupTitle = 'Shopify Configuration';
        if (popUpObj && popUpObj.showChildModal) popUpObj.showChildModal();
        return false;
      }
      const hasApiKeyPassword = (shop.apiKey && shop.apiKey.toString().trim() !== '' && shop.password && shop.password.toString().trim() !== '');
      const hasAccessToken = (shop.accessToken && shop.accessToken.toString().trim() !== '');
      if (!hasApiKeyPassword && !hasAccessToken) {
        this.popUpMessage = 'For Shopify please provide API credentials: either API Key & Password, or an Access Token.';
        this.popupTitle = 'Shopify Configuration';
        if (popUpObj && popUpObj.showChildModal) popUpObj.showChildModal();
        return false;
      }
    } else if (provider === 'digirestro') {
      const digi = ext.digirestro || {};
      if (!digi.baseUrl || digi.baseUrl.toString().trim() === '') {
        this.popUpMessage = 'Digirestro base URL is required.';
        this.popupTitle = 'Digirestro Configuration';
        if (popUpObj && popUpObj.showChildModal) popUpObj.showChildModal();
        return false;
      }
      if (!digi.apiKey || digi.apiKey.toString().trim() === '') {
        this.popUpMessage = 'Digirestro API Key is required.';
        this.popupTitle = 'Digirestro Configuration';
        if (popUpObj && popUpObj.showChildModal) popUpObj.showChildModal();
        return false;
      }
        if (!digi.browserUrl || digi.browserUrl.toString().trim() === '') {
          this.popUpMessage = 'Digirestro Browser URL is required.';
          this.popupTitle = 'Digirestro Configuration';
          if (popUpObj && popUpObj.showChildModal) popUpObj.showChildModal();
          return false;
        }
      if (!digi.integrationId || digi.integrationId.toString().trim() === '') {
        this.popUpMessage = 'Digirestro Integration ID is required.';
        this.popupTitle = 'Digirestro Configuration';
        if (popUpObj && popUpObj.showChildModal) popUpObj.showChildModal();
        return false;
      }
    }

    return true;
  }

  /**
   * Test Digirestro API connection using health check endpoint
   */
  public testDigirestroConnection(): void {
    if (!this.facility.externalApi || !this.facility.externalApi.digirestro) {
      this.digirestroTestError = 'Digirestro configuration is missing';
      this.digirestroTestResult = '';
      return;
    }

    const config = this.facility.externalApi.digirestro;

    // Validate required fields before testing
    if (!config.baseUrl || config.baseUrl.trim() === '') {
      this.digirestroTestError = 'Please enter Base URL';
      this.digirestroTestResult = '';
      return;
    }

    if (!config.apiKey || config.apiKey.trim() === '') {
      this.digirestroTestError = 'Please enter API Key';
      this.digirestroTestResult = '';
      return;
    }

    if (!config.storeId || Number(config.storeId) <= 0) {
      this.digirestroTestError = 'Please enter a valid Store ID';
      this.digirestroTestResult = '';
      return;
    }

    // Clear previous results
    this.digirestroTestError = '';
    this.digirestroTestResult = '';
    this.isTestingDigirestroConnection = true;

    // Prepare config object
    const digirestroConfig = {
      baseUrl: config.baseUrl.trim(),
      apiKey: config.apiKey.trim(),
      storeId: Number(config.storeId),
      browserUrl: config.browserUrl ? config.browserUrl.trim() : '',
      integrationId: config.integrationId ? Number(config.integrationId) : 0
    };

    // Call the unified service method
    this.linkService.testExternalApiConnection('digirestro', digirestroConfig)
      .then((result: any) => {
        this.isTestingDigirestroConnection = false;

        if (result && result.success) {
          this.digirestroTestResult = result.message || '✓ Connection successful!';
          this.digirestroTestError = '';
        } else {
          this.digirestroTestError = result && result.message
            ? result.message
            : 'Connection failed. Please verify your credentials.';
          this.digirestroTestResult = '';
        }
      })
      .catch((error: any) => {
        this.isTestingDigirestroConnection = false;
        this.digirestroTestError = error && error.message
          ? error.message
          : 'Connection failed. Please verify your credentials and network connection.';
        this.digirestroTestResult = '';
        console.error('Digirestro connection test error:', error);
      });
  }

  /**
   * Clear Digirestro test results
   */
  public clearDigirestroTestResults(): void {
    this.digirestroTestResult = '';
    this.digirestroTestError = '';
  }

  /**
 * FIXED: Merge activities from cache - finds BOTH previous and new groups
 *
 * 1. Finds all groups that currently have this activity (previous groups)
 * 2. Merges with newly selected groups (de-duplicated)
 * 3. Collects all activities from ALL merged groups
 * 4. Updates all groups with the complete activity list
 *
 * Returns a Promise resolving to update response or null on error.
 */
  public mergeActivitiesFromCacheAndUpdate(groupIds: string[], vendorId: string, activityId: string): Promise<any> {
    var self = this;

    if (!vendorId) {
      console.warn('mergeActivitiesFromCacheAndUpdate: missing vendorId, skipping');
      return Promise.resolve(null);
    }
    if (!activityId) {
      console.warn('mergeActivitiesFromCacheAndUpdate: missing activityId, skipping');
      return Promise.resolve(null);
    }

    // groupIds can be empty for new links, but not for updates
    const newGroupIds = Array.isArray(groupIds) ? groupIds : [];

    return new Promise(function (resolve) {
      try {
        var groupsArr = Array.isArray(self.groupList) ? self.groupList : [];

        // STEP 1: Find all groups that currently have this activity (previous groups)
        var previousGroupIds: string[] = [];
        for (var i = 0; i < groupsArr.length; i++) {
          var group = groupsArr[i];
          if (group && Array.isArray(group.activities)) {
            // Check if this group contains the current activity
            for (var a = 0; a < group.activities.length; a++) {
              if (String(group.activities[a]) === String(activityId)) {
                previousGroupIds.push(group._id);
                break; // Found it, move to next group
              }
            }
          }
        }

        console.log('Previous group IDs for link ' + activityId + ':', previousGroupIds);
        console.log('New group IDs from UI:', newGroupIds);

        // STEP 2: Merge previous and new group IDs (de-duplicate)
        var allGroupIdsMap: { [k: string]: boolean } = {};
        for (var i = 0; i < previousGroupIds.length; i++) {
          allGroupIdsMap[previousGroupIds[i]] = true;
        }
        for (var i = 0; i < newGroupIds.length; i++) {
          allGroupIdsMap[newGroupIds[i]] = true;
        }

        var allGroupIds: string[] = [];
        for (var gid in allGroupIdsMap) {
          if (allGroupIdsMap.hasOwnProperty(gid)) {
            allGroupIds.push(gid);
          }
        }

        console.log('Merged group IDs (previous + new):', allGroupIds);

        // If no groups at all, just skip
        if (allGroupIds.length === 0) {
          console.warn('mergeActivitiesFromCacheAndUpdate: no groups to update');
          return resolve(null);
        }

        // STEP 3: Collect all activities from these merged groups
        var activityMap: { [k: string]: boolean } = {};

        for (var i = 0; i < allGroupIds.length; i++) {
          var gid = allGroupIds[i];
          // Find group in local cache
          var found = null;
          for (var j = 0; j < groupsArr.length; j++) {
            if (groupsArr[j] && groupsArr[j]._id === gid) {
              found = groupsArr[j];
              break;
            }
          }

          if (found && Array.isArray(found.activities)) {
            // Add all activities from this group
            for (var a = 0; a < found.activities.length; a++) {
              var act = found.activities[a];
              if (act) {
                activityMap[String(act)] = true;
              }
            }
          }
        }

        // STEP 4: Include the current activity
        activityMap[String(activityId)] = true;

        // STEP 5: Convert to array
        var mergedActivities: string[] = [];
        for (var k in activityMap) {
          if (activityMap.hasOwnProperty(k)) {
            mergedActivities.push(k);
          }
        }

        console.log('Final merged activities for all groups:', mergedActivities);
        console.log('Updating ' + allGroupIds.length + ' groups with ' + mergedActivities.length + ' activities');

        // STEP 6: Update ALL groups (previous + new) with the merged activity list
        try {
          self.linkService.updateGroups(allGroupIds, vendorId, mergedActivities)
            .then(function (res: any) {
              if (!(res && (res.status === 'SUCCESS' || res.status === 'OK'))) {
                console.warn('mergeActivitiesFromCacheAndUpdate: update returned non-success:', res);
              } else {
                console.log('✓ Successfully updated ' + allGroupIds.length + ' groups with ' + mergedActivities.length + ' activities');
                console.log('✓ Groups updated:', allGroupIds);
              }
              resolve(res);
            })
            .catch(function (err: any) {
              console.warn('mergeActivitiesFromCacheAndUpdate: update failed:', err);
              resolve(null);
            });
        } catch (e) {
          console.warn('mergeActivitiesFromCacheAndUpdate: update threw', e);
          resolve(null);
        }
      } catch (ex) {
        console.warn('mergeActivitiesFromCacheAndUpdate: unexpected error', ex);
        resolve(null);
      }
    });
  }


  /* ================================================================
     CRUD: Add / Update / Delete Links
  ===================================================================*/
  public addLink(popUpObj: any): void {
    try {
      this.loaderEvent.fire(false);
      this.cancleButton = true;
      if (!this.facility) this.facility = {} as any;

      // Always ensure these defaults before validation/save
      this.facility.waitlistCount = this.facility.waitlistCount || '3';
      this.facility.maxNumberOfPeople = this.facility.maxNumberOfPeople || '999999';

      if (!this.safeValidateAndReturn()) {
        this.loaderEvent.fire(true);
        this.scrollToFirstError();
        return;
      }

      if (this.isExternalApiLink() && !this.validateExternalApiConfig(popUpObj)) {
        this.loaderEvent.fire(true);
        return;
      }

      this.cleanUpBeforeSave();
      this.setFacilityData();


      this.linkService.addLink(this.facility)
        .then((result: any) => {
          if (result && result.status === 'SUCCESS') {
            const saved = (result && result.facilityData) ? result.facilityData : null;
            const ok = (result && result.status === 'SUCCESS' && !!saved);

            if (ok) {
              console.log('links event saved successfully:', saved);

              // clear any drafts
              localStorage.removeItem('facility');
              localStorage.removeItem('currentCategory');
              this.loaderEvent.fire(true);

              // Build payload from the saved facility
              const groupIds: string[] = Array.isArray(saved.targetGroupIds) ? saved.targetGroupIds : [];
              // Prefer component method; fall back to saved/vendor
              const computedVendorId =
                (typeof this.getVendorId === 'function' && this.getVendorId()) ||
                (saved && saved.vendorId) ||
                (this.vendor ? this.vendor._id : '');

              const activityId: string = (saved && saved._id) ? String(saved._id) : '';

              if (computedVendorId && activityId && groupIds.length > 0) {

                // fire-and-forget; don't block the UX
                this.mergeActivitiesFromCacheAndUpdate(groupIds, computedVendorId, activityId)
                  .then((upd: any) => {
                    if (!(upd && upd.status === 'SUCCESS')) {
                      console.warn('links update returned non-success:', (upd && upd.message) || upd);
                    }
                  })
                  .catch((err: any) => console.warn('Group update failed:', err));
              } else {
                if (!computedVendorId) console.warn('Skipped group update: missing vendorId');
                if (!activityId) console.warn('Skipped group update: missing activityId');
                if (!groupIds.length) console.warn('Skipped group update: empty groupIds');
              }

              this.router.navigate(['/links']);
            } else {
              this.loaderEvent.fire(true);
              this.popUpMessage = (result && result.message) ? result.message : 'Failed to save event';
              popUpObj.showChildModal();
            }

            this.loaderEvent.fire(true);

            this.router.navigate(['/links']);
          } else {
            this.loaderEvent.fire(true);
            this.popUpMessage = result && result.message ? result.message : 'Failed to save link';
            if (popUpObj && typeof popUpObj.showChildModal === 'function') popUpObj.showChildModal();
          }
        })
        .catch((err: any) => {
          console.error('Error saving link:', err);
          this.loaderEvent.fire(true);
          this.popUpMessage = 'An error occurred while saving the link';
          if (popUpObj && typeof popUpObj.showChildModal === 'function') popUpObj.showChildModal();
        });
    } catch (e) {
      console.error('Exception in addLink:', e);
      this.loaderEvent.fire(true);
    }
  }

  /**
   * Verbose validation helper — prepares facility and logs detailed validation results.
   * Returns boolean valid.
   */
  private safeValidateAndReturn(showPopup: boolean = false, popUpObj?: any): boolean {
    // Ensure LinkValidation exists
    if (!this.linkValidationObj) {
      try {
        this.linkValidationObj = new LinkValidation(this.appService, this.utilityFunctions, this.timeCurrencyService);
        console.warn('linkValidationObj was missing; instantiated fallback.');
      } catch (e) {
        console.error('Could not instantiate LinkValidation:', e);
        return false;
      }
    }

    // Ensure facility exists
    if (!this.facility) {
      console.warn('safeValidateAndReturnVerbose: facility is not set');
      return false;
    }

    // Copy date/expiry date strings from dateObj/expiryDateObj if present (validator expects strings)
    try {
      if (this.facility.dateObj && (!this.facility.date || this.facility.date === '')) {
        this.facility.date = this.formatDateTimeWithZeroTime(this.facility.dateObj);
      }
    } catch (e) { /* ignore */ }

    try {
      if (this.facility.expiryDateObj && (!this.facility.expiryDate || this.facility.expiryDate === '')) {
        this.facility.expiryDate = this.formatDateTimeWithZeroTime(this.facility.expiryDateObj);
      }
    } catch (e) { /* ignore */ }

    // Ensure timings / open-close mirrors exist (validators check openTime_hour etc)
    this.openTime_hour = this.openTime_hour || '01';
    this.openTime_minute = this.openTime_minute || '00';
    this.closeTime_hour = this.closeTime_hour || '23';
    this.closeTime_minute = this.closeTime_minute || '00';
    if (!this.facility.timings) this.facility.timings = { openTime: this.openTime_hour + ':' + this.openTime_minute, closeTime: this.closeTime_hour + ':' + this.closeTime_minute };

    // Debug: show facility snapshot we are validating
    try { console.group('Validating facility snapshot'); console.log(JSON.parse(JSON.stringify(this.facility))); console.groupEnd(); } catch (e) { }

    // Run validation
    let isValid = false;
    try {
      if (typeof (this.linkValidationObj as any).validateFacilityObj === 'function') {
        isValid = (this.linkValidationObj as any).validateFacilityObj(this);
      } else {
        console.warn('validateFacilityObj not found on linkValidationObj');
        isValid = false;
      }
    } catch (err) {
      console.error('Validation threw an exception:', err);
      isValid = false;
    }

    // Gather detailed results
    try {
      const validateMap = this.linkValidationObj.validate || {};
      const errorMsgs = this.linkValidationObj.errorMessages || {};
      const requiredKeys: string[] = [];
      for (const k of Object.keys(validateMap)) {
        if (validateMap[k] === 'required') requiredKeys.push(k);
      }
      console.group('Link Validation Debug (verbose)');
      console.log('isValid:', isValid);
      console.log('validate map (all):', validateMap);
      console.log('required keys:', requiredKeys);
      console.log('errorMessages:', errorMsgs);
      try {
        if (typeof (this.linkValidationObj as any).getValidationErrorCount === 'function') {
          console.log('getValidationErrorCount():', (this.linkValidationObj as any).getValidationErrorCount());
        }
        if (typeof (this.linkValidationObj as any).hasValidationErrors === 'function') {
          console.log('hasValidationErrors():', (this.linkValidationObj as any).hasValidationErrors());
        }
      } catch (e) { }
      console.groupEnd();
    } catch (e) {
      console.warn('Failed to log validation details', e);
    }

    // If invalid, scroll to first error and optionally popup
    if (!isValid) {
      this.scrollToFirstError();
    }

    // If invalid, scroll to first error and optionally popup
    if (!isValid) {
      this.scrollToFirstError();
      if (showPopup && popUpObj && typeof popUpObj.showChildModal === 'function') {
        this.popupTitle = 'Validation Error';
        // Prefer a more helpful popup: show first non-empty error message if available
        let firstErrorMessage = 'Please fix validation errors before saving.';
        try {
          const em = this.linkValidationObj.errorMessages || {};
          for (const k of Object.keys(em)) {
            if (em[k]) { firstErrorMessage = em[k]; break; }
          }
        } catch (e) { }
        this.popUpMessage = firstErrorMessage;
        popUpObj.showChildModal();
      }
    }

    return isValid;
  }


  public updateLink(popUpObj: any): void {
    try {
      // begin UI busy state
      this.loaderEvent.fire(false);
      this.cancleButton = true;
      if (!this.facility) this.facility = {} as any;
      // Force the two values before validation
      this.facility.waitlistCount = this.facility.waitlistCount || '3';
      this.facility.maxNumberOfPeople = this.facility.maxNumberOfPeople || '999999';


      // vendor guard (same pattern as updateEvent)
      if (!this.vendor || !this.vendor._id) {
        this.loaderEvent.fire(true);
        this.popUpMessage = CONSTANT_MESSAGES.INVALID_VENDOR_ERROR;
        if (popUpObj && typeof popUpObj.showChildModal === 'function') popUpObj.showChildModal();
        return;
      }

      // validation (safe wrapper that also scrolls to first error)
      const isValid = this.safeValidateAndReturn();
      console.log('isValid', isValid)
      if (!isValid) {
        this.loaderEvent.fire(true);
        return;
      }

      // external API config guard for link type that requires it
      if (this.isExternalApiLink() && !this.validateExternalApiConfig(popUpObj)) {
        this.loaderEvent.fire(true);
        return;
      }

      console.log('IN updateLink');

      // prepare payload
      this.cleanUpBeforeSave();
      this.setFacilityData();

      // ensure createdBy fallback like updateEvent
      if (!this.facility.createdBy || this.facility.createdBy === '') {
        if (this.vendor && this.vendor.role === CONSTANT_VALUES.SUPER_ADMIN &&
          this.vendor.subVendors && this.vendor.subVendors.length > 0) {
          this.facility.createdBy = this.vendor.subVendors[0]._id;
        } else {
          this.facility.createdBy = this.vendor ? this.vendor._id : '';
        }
        console.log('Final createdBy fallback set to:', this.facility.createdBy);
      }

      // optional: some components build timings from individual controls before save
      if (typeof (this as any).buildTimingsFromComponents === 'function') {
        try {
          (this as any).buildTimingsFromComponents();
        } catch (e) {
          console.warn('buildTimingsFromComponents failed', e);
        }
      }

      // STEP 1: Update the link in the database first
      this.linkService.updateLink(this.facility)
        .then((result: any) => {
          if (result && result.status === 'SUCCESS') {
            console.log('Link updated successfully');

            // STEP 2: Now update the group associations
            const groupIds = this.facility.targetGroupIds || [];
            const vendorId = this.getVendorId() || '';
            const activityId = this.facility._id;

            if (groupIds.length > 0 && vendorId && activityId) {
              // Update groups with merged activities (previous + new)
              this.mergeActivitiesFromCacheAndUpdate(groupIds, vendorId, activityId)
                .then((groupUpdateResult: any) => {
                  if (groupUpdateResult && groupUpdateResult.status === 'SUCCESS') {
                    console.log('Groups updated successfully after link update');
                  } else {
                    console.warn('Group update returned non-success:', groupUpdateResult);
                  }

                  // Clear cache and navigate regardless of group update result
                  try { localStorage.removeItem('facility'); } catch (e) { /* ignore */ }
                  this.loaderEvent.fire(true);
                  this.router.navigate(['/links']);
                })
                .catch((err: any) => {
                  console.warn('Group update after link update failed:', err);

                  // Still navigate - link was saved successfully
                  try { localStorage.removeItem('facility'); } catch (e) { /* ignore */ }
                  this.loaderEvent.fire(true);
                  this.router.navigate(['/links']);
                });
            } else {
              // No groups to update
              if (!vendorId) console.warn('Skipped group update: missing vendorId');
              if (!activityId) console.warn('Skipped group update: missing activityId');
              if (!groupIds.length) console.warn('Skipped group update: empty groupIds');

              try { localStorage.removeItem('facility'); } catch (e) { /* ignore */ }
              this.loaderEvent.fire(true);
              this.router.navigate(['/links']);
            }
          } else {
            // Link update failed
            this.loaderEvent.fire(true);
            this.popUpMessage = result && result.message ? result.message : 'Failed to update link';
            if (popUpObj && typeof popUpObj.showChildModal === 'function') popUpObj.showChildModal();
          }
        })
        .catch((err: any) => {
          console.error('updateLink error:', err);
          this.loaderEvent.fire(true);
          this.popUpMessage = 'An error occurred while updating the link';
          if (popUpObj && typeof popUpObj.showChildModal === 'function') popUpObj.showChildModal();
        });
    } catch (err) {
      console.error(err);
      this.loaderEvent.fire(true);
    }
  }


  public deleteFacility(): void {
    try {
      const facility = JSON.parse(this.appService.getFacility());
      const deleteFacilityObj = { facilityId: facility._id, vendorId: '' };

      if (this.vendor && this.vendor.role === CONSTANT_VALUES.SUPER_ADMIN && this.vendor.subVendors && this.vendor.subVendors.length > 0) {
        deleteFacilityObj.vendorId = this.vendor.subVendors[0]._id;
      } else {
        deleteFacilityObj.vendorId = this.vendor ? this.vendor._id : '';
      }

      this.loaderEvent.fire(false);
      this.linkService.deleteLink(deleteFacilityObj).then((result: any) => {
        if (result.status === 'SUCCESS') {
          this.router.navigate(['/links']);
        } else {
          this.loaderEvent.fire(true);
          this.popUpMessage = result.message;
        }
      }).catch((err: any) => {
        console.log(err);
        this.loaderEvent.fire(true);
      });
    } catch (err) {
      this.loaderEvent.fire(true);
      console.log(err);
    }
  }

  /* ================================================================
     State management / helpers
  ===================================================================*/
  public setFacilityData(): void {
    if (!this.facility) return;

    if (!this.facility.timings) {
      this.facility.timings = { openTime: '01:00', closeTime: '23:00' };
    }
    if (this.vendor) {
      this.facility.timezoneOffset = this.vendor.timezoneOffset;
      this.facility.timezoneUTC = this.vendor.timezoneUTC;
      this.facility.timezoneAbbr = this.vendor.timezoneAbbr;

      if (!this.facility.vendorId || this.facility.vendorId === '') {
        if (this.vendor.role === CONSTANT_VALUES.SUPER_ADMIN &&
          this.vendor.subVendors && this.vendor.subVendors.length > 0) {
          this.facility.vendorId = this.vendor.subVendors[0]._id;
          this.facility.createdBy = this.vendor.subVendors[0]._id;
        } else {
          this.facility.vendorId = this.vendor._id;
          this.facility.createdBy = this.vendor._id;
        }
      }
    }

    if (this.facility.expiryDateObj) {
      this.facility.expiryDate = this.formatDateTimeWithZeroTime(this.facility.expiryDateObj);
    }

    if (this.facility.dateObj) {
      this.facility.date = this.formatDateTimeWithZeroTime(this.facility.dateObj);
    }

    if (this.facility.isSlotsLocked) {
      const openHours = (this.openTime_hour || '01');
      const openMinutes = (this.openTime_minute || '00');
      this.facility.activitySlots = [openHours + ':' + openMinutes];
    } else {
      this.facility.activitySlots = [];
    }

    if (!this.facility.slotDuration || this.facility.slotDuration === '') {
      this.facility.slotDuration = (22 * 60).toString();
    }

    this.facility.peekTimings = [];
    this.facility.breakTimings = [];
    this.facility.peakUnitsCount = '0';
    this.facility.noOfUnits = '1';
    this.facility.unitNames = ['link'];

    if (!this.facility.targetGroupIds || this.facility.targetGroupIds.length === 0) {
      this.facility.isCreatedForGroup = false;
    }

    const currencyInfo = this.timeCurrencyService.getTimeCurrencyInfo(this.vendor ? this.vendor.country : 'Singapore');
    if (this.facility.amount) {
      this.facility.amount.currency = currencyInfo.currency;
    }
  }

  private resetLocalState(): void {
    // Defensive vendor/currency info
    const vendorCountry = (this.vendor && this.vendor.country) ? this.vendor.country : 'Singapore';
    const currencyInfo = this.timeCurrencyService
      ? this.timeCurrencyService.getTimeCurrencyInfo(vendorCountry)
      : { currency: 'SGD', code: '702', symbol: 'S$', name: 'Singapore Dollar', country: 'Singapore', timezone: 'Asia/Singapore', timezoneAbbr: 'SGT', minorUnit: 'Cent' };

    // canonical today at midnight
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Immediately create a fresh facility object so subsequent reads are safe
    this.facility = {
      _id: '',
      name: '',
      category: 'Links',
      linkType: '',
      // canonical date string and Date object
      date: this.formatDateTimeWithZeroTime(today),
      dateObj: new Date(today),
      expiryDateObj: null,
      expiryDate: '',
      location: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      pincode: '',
      country: (this.vendor && this.vendor.country) ? this.vendor.country : 'Singapore',
      icon: '',
      notes: '',
      description: '',
      ctaText: '',
      ctaLink: '',
      ctaEnabled: false,
      maxBookingsPerSlot: '',
      timings: { openTime: '01:00', closeTime: '23:00' },
      noOfUnits: '1',
      slotDuration: '',
      unitNames: ['link'],
      targetGroupIds: [],
      isAllowForceBooking: false,

      activityCharges: {
        member: {
          enabled: false,
          amount: '',
          childEnabled: false,
          childAmount: '',
          currency: currencyInfo.currency,
          currencyCode: currencyInfo.code,
          currencySymbol: currencyInfo.symbol,
          currencyName: currencyInfo.name,
          minorUnit: currencyInfo.minorUnit,
          country: currencyInfo.country
        },
        nonMember: {
          enabled: false,
          amount: '',
          childEnabled: false,
          childAmount: '',
          currency: currencyInfo.currency,
          currencyCode: currencyInfo.code,
          currencySymbol: currencyInfo.symbol,
          currencyName: currencyInfo.name,
          minorUnit: currencyInfo.minorUnit,
          country: currencyInfo.country
        },
        guest: {
          enabled: false,
          amount: '',
          childEnabled: false,
          childAmount: '',
          currency: currencyInfo.currency,
          currencyCode: currencyInfo.code,
          currencySymbol: currencyInfo.symbol,
          currencyName: currencyInfo.name,
          minorUnit: currencyInfo.minorUnit,
          country: currencyInfo.country
        },
        corpMember: {
          enabled: false,
          amount: '',
          currency: currencyInfo.currency,
          currencyCode: currencyInfo.code,
          currencySymbol: currencyInfo.symbol,
          currencyName: currencyInfo.name,
          minorUnit: currencyInfo.minorUnit,
          country: currencyInfo.country
        },
        corpNonMember: {
          enabled: false,
          amount: '',
          currency: currencyInfo.currency,
          currencyCode: currencyInfo.code,
          currencySymbol: currencyInfo.symbol,
          currencyName: currencyInfo.name,
          minorUnit: currencyInfo.minorUnit,
          country: currencyInfo.country
        }
      } as ActivityCharges,

      isGSTApplicable: false,
      GSTValue: '0.00',

      isChargable: false,
      amount: { currency: currencyInfo.currency, value: '' },

      isHoldBooking: false,
      holdBookingDays: '',

      reminder: { unit: 'hours', value: 480 },
      notificationAcceptanceHour: 20,

      isCreatedForGroup: false,

      vendorId: '',
      isActive: true,
      createdBy: '',
      timezoneOffset: (this.vendor && typeof this.vendor.timezoneOffset !== 'undefined') ? this.vendor.timezoneOffset : new Date().getTimezoneOffset(),
      timezoneUTC: (this.vendor && this.vendor.timezoneUTC) ? this.vendor.timezoneUTC : '',
      timezoneAbbr: (this.vendor && this.vendor.timezoneAbbr) ? this.vendor.timezoneAbbr : '',

      isPeakTimeEnabled: false,
      isBreakTimeEnabled: false,
      isSlotsLocked: true,

      isMaxBookingPerMembership: false,
      isMaxBookingPerPerson: false,
      maxNumberOfPeople: '999999',
      waitlistCount: '3',
      maxWeekdayBooking: '',
      maxWeekendBooking: '',
      maxBookingHoursWeekday: '',
      maxBookingHoursWeekend: '',
      maxBookingHoursMonth: '',

      peekStartTime: '',
      peekEndTime: '',
      peakUnitsCount: '',
      breakTimings: [],
      peekTimings: [],

      activitySlots: [],
      isCourtSelection: false,
      isOpenBookingOnFriday: false,
      isOpenNextMonthBooking: false,
      isWeeklyRepeatBooking: false,

      isAllowMemberNotes: false,
      memberNotesHintText: '',
      vendorNotesHintText: '',
      isAllowVendorNotes: false,
      showEventActivity: false
    } as LinkFacility;

    // Ensure mirror fields used by template/controllers are set
    this.openTime_hour = '01';
    this.openTime_minute = '00';
    this.closeTime_hour = '23';
    this.closeTime_minute = '00';

    this.isGSTApplicable = false;
    this.viewIcon = { name: '', iconClass: '' };
    this.redirectConfirmationMessage = CONSTANT_MESSAGES.REDIRECT_FROM_ACTIVITY;

    // ensure calendarExpiryDate exists and is null (template expects Date or null)
    try {
      this.calendarExpiryDate = null;
    } catch (e) { /* ignore */ }
    this.ensureTargetGroupIds();
    // Update currency display
    this.updateCurrencyDisplay();
  }
  private cleanUpBeforeSave(): void {
    if (!this.facility) return;
    this.facility.name = (this.facility.name || '').trim();
    this.facility.notes = (this.facility.notes || '').trim();
    if (this.facility.GSTValue) this.facility.GSTValue = String(this.facility.GSTValue);
    if (!this.facility['externalApi']) {
      this.facility['externalApi'] = { enabled: false, provider: '', shopify: {}, custom: {} } as any;
    }
  }

  /* ================================================================
     Modal helpers / navigation / validation
  ===================================================================*/
  public cancelLink(popUpObj: any): void {
    this.popUpMessage = 'Are you sure you want to cancel? All unsaved changes will be lost.';
    this.popupTitle = 'Confirm Cancel';
    this.cancleButton = true;
    if (popUpObj && popUpObj.showChildModal) {
      popUpObj.showChildModal();
    }
  }

  public changeCancleButton(popUpObj: any) {
    try {
      if (this.deleteMessage) {
        this.popupTitle = CONSTANT_MESSAGES.DELETE_CONFIRMATION_TITLE;
        this.popUpMessage = CONSTANT_MESSAGES.DELETE_CONFIRMATION;
      } else {
        this.popupTitle = CONSTANT_MESSAGES.EDIT_CONFIRMATION_TITLE;
        this.popUpMessage = CONSTANT_MESSAGES.EDIT_CONFIRMATION;
      }
      this.cancleButton = false;
      this.cancleFlag = true;
      popUpObj.showChildModal();
    } catch (err) {
      console.log(err);
    }
  }

  public goBackToLinksDashboard(): void {
    if (this.componentDestroyed) return;
    try { localStorage.removeItem('facility'); } catch (e) { /* ignore */ }
    try { localStorage.removeItem('currentCategory'); } catch (e) { /* ignore */ }
    this.router.navigate(['/links']);
  }

  private scrollToFirstError(): void {
    setTimeout(() => {
      try {
        const firstError = document.querySelector('.validation-error:not(:empty), .invalid');
        if (firstError) (firstError as any).scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) { /* ignore */ }
    }, 100);
 }

  private setFrameForFacilityRules(): void {
    if (localStorage.getItem('facility')) {
      this.viewFacility('facility');
    } else if (localStorage.getItem('currentCategory')) {
      try {
        this.currentCategory = JSON.parse(atob(localStorage.getItem('currentCategory')));
      } catch (e) { this.currentCategory = null; }
      this.viewFacility('category');
    } else {
      this.pageName = 'Create Link';
           this.addLinkStatus = true;
    }
  }

  /**
   * Populate this.facility from local storage / appService depending on frameName,
   * and initialize derived fields so template inputs/toggles display correctly.
   */
  private setFacilityForView(frameName: string): void {
    console.log('setFacilityForView frameName', frameName);

    // Local helper: robust parse to Date or null
    var parseToDateOrNull = function (raw: any): Date {
      if (raw === null || typeof raw === 'undefined' || raw === '') return null;
      if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
      var s = ('' + raw).trim();
      if (!s) return null;

      // 1) Native parse (handles ISO-like strings)
      var d = new Date(s);
      if (!isNaN(d.getTime())) return d;

      // 2) Replace first space -> 'T' for "YYYY-MM-DD HH:MM:SS(.sss)"
      d = new Date(s.replace(' ', 'T'));
      if (!isNaN(d.getTime())) return d;

      // 3) YYYY-MM-DD or YYYY/MM/DD
      var m = s.match(/^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})$/);
      if (m) {
        var y = +m[1], mo = +m[2] - 1, da = +m[3];
        var dd = new Date(y, mo, da);
        return isNaN(dd.getTime()) ? null : dd;
      }

      // 4) DD-MM-YYYY or DD/MM/YYYY
      m = s.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
      if (m) {
        var da = +m[1], mo = +m[2] - 1, y = +m[3];
        var dd = new Date(y, mo, da);
        return isNaN(dd.getTime()) ? null : dd;
      }

      // 5) Datetime with HH:MM:SS optionally .sss
      m = s.match(/^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2})(?:\.(\d{1,3}))?)?$/);
      if (m) {
        var y = +m[1], mo = +m[2] - 1, da = +m[3],
          hh = +m[4], mm = +m[5], ss = m[6] ? +m[6] : 0, ms = m[7] ? +m[7] : 0;
        var dd = new Date(y, mo, da, hh, mm, ss, ms);
        return isNaN(dd.getTime()) ? null : dd;
      }

      return null;
    };

    // Helper: normalize time string to HH:MM format (handles "16:00:00:00" -> "16:00")
    var normalizeTimeString = function (timeStr: string): string {
      if (!timeStr) return '00:00';
      var parts = timeStr.split(':');
      if (parts.length >= 2) {
        var hours = parts[0];
        var minutes = parts[1];
        // Pad with zeros
        if (hours.length === 1) hours = '0' + hours;
        if ( minutes.length === 1) minutes = '0' + minutes;
        // Take first 2 chars
        hours = hours.slice(0, 2);
        minutes = minutes.slice(0, 2);
        return hours + ':' + minutes;
      }
      return '00:00';
    };

    // Load ALL facilities from vendor_linksData first (for both frames)
    try {
      var vendorLinksRaw = localStorage.getItem('vendor_linksData');
      if (vendorLinksRaw) {
        var vendorLinks = JSON.parse(vendorLinksRaw);
        if (Array.isArray(vendorLinks) && vendorLinks.length > 0) {
          // Always load ALL facilities into facility_list
          this.facility_list = vendorLinks;
        } else {
          this.facility_list = [];
        }
      } else {
        this.facility_list = [];
      }
    } catch (e) {
      console.warn('Failed to load vendor_linksData', e);
      this.facility_list = [];
    }

    // 1) Load facility based on frameName
    if (frameName === 'facility') {
      try {
        this.facility = JSON.parse(this.appService.getFacility()) as LinkFacility;
      } catch (e) {
        console.warn('Failed to load facility for facility frame', e);
        // Fallback to first facility from list if available
        if (this.facility_list && this.facility_list.length > 0) {
          this.facility = this.facility_list[0];
        } else {
          this.resetLocalState();
          return;
        }
      }
    } else if (frameName === 'category') {
      // Set current facility from appService or use first one as default
      try {
        var storedFacility = this.appService.getFacility();
        if (storedFacility) {
          this.facility = JSON.parse(storedFacility) as LinkFacility;
        } else {
          // Default to first facility if none stored
          if (this.facility_list && this.facility_list.length > 0) {
            this.facility = this.facility_list[0];
          } else {
            this.resetLocalState();
            return;
          }
        }
      } catch (e) {
        console.warn('Failed to load facility for category frame', e);
        // Fallback to first facility
        if (this.facility_list && this.facility_list.length > 0) {
          this.facility = this.facility_list[0];
        } else {
          this.resetLocalState();
          return;
        }
      }
    } else {
      // Unknown frameName
      this.resetLocalState();
      return;
    }

    // 2) Initialize nested objects if missing
    if (!this.facility.activityCharges) {
      this.facility.activityCharges = {} as ActivityCharges;
    }

    if (!this.facility.timings) {
      this.facility.timings = { openTime: '01:00', closeTime: '23:00' };
    } else {
      // Normalize timings format (handles "16:00:00:00" -> "16:00")
      if (this.facility.timings.openTime) {
        this.facility.timings.openTime = normalizeTimeString(this.facility.timings.openTime);
      }
      if (this.facility.timings.closeTime) {
        this.facility.timings.closeTime = normalizeTimeString(this.facility.timings.closeTime);
      }
    }

    // 3) Calendar-friendly Date objects
    try {
      if (this.facility.date && !this.facility.dateObj) {
        var parsedDate = parseToDateOrNull(this.facility.date);
        this.facility.dateObj = parsedDate ? parsedDate : new Date();
      }
    } catch (e) {
      console.warn('Failed to parse facility.date', e);
    }

    try {
      // Normalize expiry into expiryDateObj (Date or null) and sync calendarExpiryDate
      var rawExpiryCandidate = this.facility.expiryDateObj || this.facility.expiryDate || null;
      var parsedExpiry = parseToDateOrNull(rawExpiryCandidate);

      if (parsedExpiry) {
        parsedExpiry.setHours(0, 0, 0, 0); // date-only semantics
        this.facility.expiryDateObj = parsedExpiry;
        try {
          this.facility.expiryDate = this.formatDateTimeWithZeroTime(parsedExpiry);
        } catch (e) {
          console.warn('Failed to format expiryDate', e);
        }
      } else {
        this.facility.expiryDateObj = null;
      }

      try {
        this.calendarExpiryDate = this.facility.expiryDateObj
          ? new Date(this.facility.expiryDateObj.getTime())
          : null;
      } catch (e) {
        this.calendarExpiryDate = null;
      }
    } catch (e) {
      this.facility.expiryDateObj = null;
      this.calendarExpiryDate = null;
    }

    // 4) Icon preview
    try {
      if (CONSTANT_VALUES.ICON_LIST_IMAGE && Array.isArray(CONSTANT_VALUES.ICON_LIST_IMAGE)) {
        this.iconImage = CONSTANT_VALUES.ICON_LIST_IMAGE.slice();
      } else {
        this.iconImage = [];
      }

      var iconName = this.facility.icon || '';
      if (this.iconImage.length > 0) {
        var match = null;
        for (var i = 0; i < this.iconImage.length; i++) {
          if (this.iconImage[i] && this.iconImage[i].name === iconName) {
            match = this.iconImage[i];
            break;
          }
        }
        if (match) {
          this.viewIcon = { name: match.name, iconClass: match.iconClass };
        } else {
          this.viewIcon = { name: '', iconClass: '' };
        }
      } else {
        this.viewIcon = { name: '', iconClass: '' };
      }
    } catch (e) {
      this.viewIcon = { name: '', iconClass: '' };
    }

    // 5) Timings split for inputs
    try {
      var open = (this.facility.timings && this.facility.timings.openTime)
        ? this.facility.timings.openTime
        : '01:00';
      var close = (this.facility.timings && this.facility.timings.closeTime)
        ? this.facility.timings.closeTime
        : '23:00';

      var openParts = open.split(':');
      var closeParts = close.split(':');

      var openHour = openParts[0] || '01';
      var openMin = openParts[1] || '00';
      var closeHour = closeParts[0] || '23';
      var closeMin = closeParts[1] || '00';

      // Pad and slice
      if (openHour.length === 1) openHour = '0' + openHour;
      if (openMin.length === 1) openMin = '0' + openMin;
      if (closeHour.length === 1) closeHour = '0' + closeHour;
      if (closeMin.length === 1) closeMin = '0' + closeMin;

      this.openTime_hour = openHour.slice(0, 2);
      this.openTime_minute = openMin.slice(0, 2);
      this.closeTime_hour = closeHour.slice(0, 2);
      this.closeTime_minute = closeMin.slice(0, 2);
    } catch (e) {
      console.warn('Failed to split timings', e);
    }

    // 6) Initialize pricing and toggles
    try {
      this.initializePricingWithDecimals();
    } catch (e) {
      console.warn('Failed to initialize pricing', e);
    }

    try {
      this.updateShowEventPricingForLinkType(this.facility.linkType);
      this.showExternalApiConfig = this.isExternalApiLink(this.facility);
    } catch (e) {
      console.warn('Failed to update link type toggles', e);
    }

    try {
      var tiers = ['member', 'nonMember', 'guest', 'corpMember', 'corpNonMember'];
      for (var i = 0; i < tiers.length; i++) {
        var t = tiers[i];
        var p = (this.facility.activityCharges as any)[t];
        if (p && typeof p.enabled !== 'undefined') {
          this.setToggleState(t, !!p.enabled);
        } else {
          this.setToggleState(t, false);
        }
        if (t === 'member' || t === 'nonMember' || t === 'guest') {
          var childEnabled = (p && typeof p.childEnabled !== 'undefined')
            ? !!p.childEnabled
            : false;
          this.setChildToggleState(t, childEnabled);
        }
      }

      if (typeof this.facility.isGSTApplicable !== 'undefined') {
        this.toggle(this, 17, !!this.facility.isGSTApplicable);
      }
      if (typeof this.facility.isCreatedForGroup !== 'undefined') {
        this.toggle(this, 15, !!this.facility.isCreatedForGroup);
      }
      if (typeof this.facility.isAllowForceBooking !== 'undefined') {
        this.toggle(this, 10, !!this.facility.isAllowForceBooking);
      }
      if (typeof this.facility.isWaitlistEnabled !== 'undefined') {
        this.toggle(this, 2, !!this.facility.isWaitlistEnabled);
      }
      if (typeof this.facility.isAttendeeCountRequired !== 'undefined') {
        this.toggle(this, 1, !!this.facility.isAttendeeCountRequired);
      }
    } catch (e) {
      console.warn('Failed to set toggles', e);
    }

    // 7) Run validation safely
    try {
      this.safeValidateFacilityObj();
    } catch (e) {
      console.warn('safeValidateFacilityObj failed', e);
    }

    // 8) Load target groups from backend if facility has an ID
    try {
      if (this.facility && this.facility._id) {
        this.loadTargetGroupsFromActivities();
      }
    } catch (e) {
      console.warn('Failed to load target groups', e);
    }

    // 9) Calendar objects prepared
    try {
      this.initializeDateForCalendar();
    } catch (e) {
      console.warn('Failed to initialize calendar dates', e);
    }
  }

  confirmForChangeActivity(facilityId: string, popUpObj: any) {
    this.tempFacilityId = facilityId;
    if (this.editStatus) {
      this.popUpMessage = this.redirectConfirmationMessage;
      console.log('confirmForChangeActivity  this.popUpMessage', this.popUpMessage);
      this.popupTitle = 'Unsaved Changes';
      this.cancleButton = true;
      // Store the facility ID for later use
      localStorage.setItem('pendingFacilityNavigation', facilityId);

      popUpObj.showChildModal();
    } else {
      this.showFacility(this.tempFacilityId);
    }
  }

  /**
   * Mirror of old facility helper used in other components
   */
  public setFlagBeforeViewEvent(component: any): void {
    component.viewStatus = true;
    component.addLinkStatus = false;
    component.editStatus = false;
    component.isReadOnly = true;
  }

  public setDefaultValues(component: any): void {
    component.isGSTApplicable = component.facility && component.facility.isGSTApplicable || false;
  }

  /**
   * Safe wrapper to call validation's validateFacilityObj
   */
  private safeValidateFacilityObj(): void {
    if (!this.linkValidationObj) {
      try {
        this.linkValidationObj = new LinkValidation(this.appService, this.utilityFunctions, this.timeCurrencyService);
      } catch (e) {
        console.warn('Could not instantiate LinkValidation in safeValidateFacilityObj:', e);
      }
    }

    if (this.linkValidationObj && typeof (this.linkValidationObj as any).validateFacilityObj === 'function') {
      try {
        (this.linkValidationObj as any).validateFacilityObj(this);
      } catch (err) {
        console.warn('validateFacilityObj threw an error', err);
      }
    } else {
      // try the single-field validator to at least populate messages
      if (this.linkValidationObj && typeof (this.linkValidationObj as any).validateFacility === 'function') {
        try {
          (this.linkValidationObj as any).validateFacility(this, 'init', null);
        } catch (e) { /* ignore */ }
      }
    }
  }

  /* ================================================================
    SELECT MULTIPLE GROUPS
 ===================================================================*/

  toggleGroupSelection(groupId: string, evt?: Event) {
    if (!this.facility) return;
    this.ensureTargetGroupIds();

    const idx = this.facility.targetGroupIds.indexOf(groupId);
    if (idx === -1) {
      this.facility.targetGroupIds.push(groupId);
    } else {
      this.facility.targetGroupIds.splice(idx, 1);
    }

    // stop propagation if triggered from a click event on checkbox
    if (evt) {
      evt.stopPropagation();
    }
  }

  // Check if a given group is selected
  isGroupSelected(groupId: string): boolean {
    if (!this.facility || !this.facility.targetGroupIds) return false;
    return this.facility.targetGroupIds.indexOf(groupId) !== -1;
  }

  // Select all visible groups
  selectAllGroups(evt?: Event) {
    if (!this.facility) return;
    this.facility.targetGroupIds = this.groupList.map(g => g._id);
    if (evt) evt.stopPropagation();
  }
  // Clear all selection
  clearAllGroups(evt?: Event) {
    if (!this.facility) return;
    this.facility.targetGroupIds = [];
    if (evt) evt.stopPropagation();
  }


  // Label to show on the dropdown button
  getSelectedGroupLabel(): string {
    if (!this.groupList || this.groupList.length === 0) {
      return 'Error Getting Groups Please refresh!'; // replace with your stringConstants.TEXT_SELECT if needed
    }
    if (!this.facility || !this.facility.targetGroupIds || this.facility.targetGroupIds.length === 0) {
      return 'Group Selected (0)'; // or stringConstants.TEXT_SELECT
    }
    // Show up to 2 names then "... (N)"
    const labels: string[] = [];
    for (let id of this.facility.targetGroupIds) {
      const g = this.groupList.find(x => x._id === id);
      if (g) labels.push(g.name);
      if (labels.length >= 2) break;
    }
    if (this.facility.targetGroupIds.length > 2) {
      return labels.join(', ') + ' ... (' + this.facility.targetGroupIds.length + ')';
    }
    return labels.join(', ');
  }

  private ensureTargetGroupIds() {
    if (!this.facility) return;
    if (!this.facility.targetGroupIds || !Array.isArray(this.facility.targetGroupIds)) {
      this.facility.targetGroupIds = [];
    }
  }

  /**
   * Load target groups by checking which groups contain this facility's activity ID
   * This is used when editing/viewing a link to show which groups it's associated with
   */
  private loadTargetGroupsFromActivities(): void {
    if (!this.facility || !this.facility._id || !this.groupList || this.groupList.length === 0) {
      return;
    }
    const activityId = this.facility._id;
    const targetGroupIds: string[] = [];
    for (let i = 0; i < this.groupList.length; i++) {
      const group = this.groupList[i];
      if (group && Array.isArray(group.activities)) {
        if (group.activities.indexOf(activityId) !== -1) {
          targetGroupIds.push(group._id);
        }
      }
    }
    this.facility.targetGroupIds = targetGroupIds;
    if (targetGroupIds.length > 0) {
      this.facility.isCreatedForGroup = true;
      this.isCreatedForGroup = true;
      console.log('Link loaded with target groups from activities:', targetGroupIds);
    }
  }

  /**
   * Get vendor ID based on role
   */
  private getVendorId(): string {
    if (this.vendor.role === CONSTANT_VALUES.SUPER_ADMIN) {
      if (this.vendor.subVendors && this.vendor.subVendors.length > 0) {
        return this.vendor.subVendors[0]._id;
      }
    }
    return this.vendor._id;
  }

  getGroupListByVendor() {
    if (!this.vendor || !this.vendor._id) {
      console.warn('Vendor ID is missing, cannot fetch groups');
      return;
    }
    let vendorObj = {
      vendorId: this.getVendorId()
    };

    this.appService.getListOfGroup(vendorObj).then((result: any) => {
      if (result.status === 'SUCCESS') {
        this.groupList = result.groups;
      }
    });
  }

  /* ================================================================
     CTA / expiry / date helpers
  ===================================================================*/
  public isLinkExpired(): boolean {
    if (!this.facility || !this.facility.expiryDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(this.facility.expiryDate);
    expiry.setHours(0, 0, 0, 0);
    return expiry < today;
  }

  public onExpiryDateSelect(selectedDate: Date): void {
    if (selectedDate && this.facility) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);
      if (selectedDate <= today) {
        this.setExpiryDateValidationError('Expiry date must be in the future');
        this.facility.expiryDate = '';
        this.facility.expiryDateObj = null;
        this.calendarExpiryDate = null;
        return;
      }

      const maxFutureDate = new Date(); maxFutureDate.setFullYear(maxFutureDate.getFullYear() + 2);
      maxFutureDate.setHours(23, 59, 59, 999);
      if (selectedDate > maxFutureDate) {
        this.setExpiryDateValidationError('Expiry date cannot be more than 2 years in the future');
        this.facility.expiryDate = '';
        this.facility.expiryDateObj = null;
        this.calendarExpiryDate = null;
        return;
      }

      const normalizedDate = new Date(selectedDate);
      normalizedDate.setHours(0, 0, 0, 0);
      this.facility.expiryDate = this.formatDateTimeWithZeroTime(normalizedDate);
      this.facility.expiryDateObj = new Date(normalizedDate);
      this.calendarExpiryDate = new Date(normalizedDate);
      this.clearExpiryDateValidationError();
    }
  }

  public onExpiryCalendarClear(): void {
    if (this.facility) {
      this.facility.expiryDate = '';
      this.facility.expiryDateObj = null;
      this.calendarExpiryDate = null;
      this.setExpiryDateValidationError('Expiry date is required');
      if (this.linkValidationObj) {
        try { this.linkValidationObj.validateFacility(this, 'facility.expiryDate', ''); } catch (e) { /* ignore */ }
      }
    }
  }


  private setExpiryDateValidationError(message: string): void {
    if (!this.linkValidationObj) return;
    if (!this.linkValidationObj.validate) {
      this.linkValidationObj.validate = {} as { [key: string]: string };
    }
    this.linkValidationObj.validate['validExpiryDate'] = 'error';
    if (!this.linkValidationObj.errorMessages) this.linkValidationObj.errorMessages = {} as { [key: string]: string };
    this.linkValidationObj.errorMessages['expiryDateError'] = message;
  }

  private clearExpiryDateValidationError(): void {
    if (!this.linkValidationObj) return;
    if (!this.linkValidationObj.validate) this.linkValidationObj.validate = {} as { [key: string]: string };
    this.linkValidationObj.validate['validExpiryDate'] = 'success';
    if (!this.linkValidationObj.errorMessages) this.linkValidationObj.errorMessages = {} as { [key: string]: string };
    this.linkValidationObj.errorMessages['expiryDateError'] = '';
  }

  /* ================================================================
     Misc / debugging
  ===================================================================*/
  public changeStatusATView(): void {
    try {
      this.isReadOnly = true;
      this.editStatus = false;
      if (typeof this.setDefaultValues === 'function') {
        this.setDefaultValues(this);
      } else {
        this.isGSTApplicable = !!(this.facility && this.facility.isGSTApplicable);
      }
    } catch (err) {
      console.warn('changeStatusATView failed', err);
    }
  }

  public confirmDelete(): void {
    this.deleteMessage = true;
  }

  public confirmationForDataLoss(confirm: boolean): void {
    if (confirm) {
      if (localStorage.getItem('vendor')) {
        localStorage.removeItem('facility');
        localStorage.removeItem('currentCategory');
        this.router.navigate(['/links']);
      }
    }
  }

  public dataResetWhileHomeScreen(confirm: boolean): void {
    if (confirm) {
      if (localStorage.getItem('vendor')) {
        localStorage.removeItem('facility');
        localStorage.removeItem('currentCategory');
        if (this.utilityFunctions && typeof this.utilityFunctions.homeScreen === 'function') {
          this.utilityFunctions.homeScreen(this);
        } else {
          this.router.navigate(['/links']);
        }
      }
    }
  }

  public showFacility(facilityId: string): void {
    this.tempFacilityId = facilityId;
    this.changeStatusATView && this.changeStatusATView();
    const found = (this.facility_list || []).filter((rec: any) => rec && rec._id === facilityId);
    if (found && found.length > 0) {
      localStorage.setItem('facility', JSON.stringify(found[0]));
      try {
        this.viewFacility('facility');
      } catch (e) {
        this.facility = found[0];
        try { this.safeValidateFacilityObj(); } catch (e2) { /* ignore */ }
      }
    } else {
      console.warn('showFacility: facility not found for id', facilityId);
    }
  }

  private navigateToFacilityDirect(facilityId: string): void {
    try {
      let targetFacility = null;

      // Find the target facility in the list
      for (let i = 0; i < this.facility_list.length; i++) {
        if (this.facility_list[i]._id === facilityId) {
          targetFacility = this.facility_list[i];
          break;
        }
      }

      if (targetFacility) {
        // Store the facility and navigate
        localStorage.setItem('facility', JSON.stringify(targetFacility));

        // Reset component state
        this.editStatus = false;
        this.viewStatus = true;
        this.isReadOnly = true;

        // Update current facility
        this.facility = targetFacility;

        // Set toggles for the new facility
        this.setToggleForViewFacility();

        // Update UI components
        this.setDefaultValues(this);
        this.initializeDateForCalendar();

        console.log('Navigated to facility:', targetFacility.name);
      } else {
        console.error('Facility not found:', facilityId);
      }
    } catch (error) {
      console.error('Error navigating to facility:', error);
    }
  }

  confirmationFromVendor(message: boolean): void {
    if (this.componentDestroyed) return;
    console.log('confirmationFromVendor message', message);
    try {
      if (message === true) {
        // Check if there's a pending navigation
        const pendingNavigation = localStorage.getItem('pendingFacilityNavigation');
        if (pendingNavigation) {
          localStorage.removeItem('pendingFacilityNavigation');
          this.navigateToFacilityDirect(pendingNavigation);
          return;
        }

        if (this.deleteMessage) {
          this.deleteFacility();
          this.deleteMessage = false;
          return;
        } else {
          this.router.navigate(['/links']);
        }
        if (this.saveFlag) {
          this.router.navigate(['/addLinks']);
        }
        if (this.cancleFlag) {
          this.router.navigate(['/links']);
          this.cancleFlag = false;
        }
      } else {
        // User chose not to proceed
        this.deleteMessage = false;
        localStorage.removeItem('pendingFacilityNavigation');
      }
    } catch (err) {
      console.log(err);
    }
  }

  public validateRequiredFields(): boolean {
    if (!this.facility || !this.facility.name || !this.facility.date || !this.facility.icon) return false;
    return true;
  }

  public isChildPricingAvailable(tier: string): boolean {
    return ['member', 'nonMember', 'guest'].indexOf(tier) !== -1;
  }

  public isChildPricingEnabled(tier: string): boolean {
    if (!this.facility || !this.facility.activityCharges) return false;
    const p = (this.facility.activityCharges as any)[tier];
    return !!(p && p.childEnabled);
  }

  public getShowEventPricing(): boolean {
    return this.showEventPricing;
  }

  public getShowExternalApiConfig(): boolean {
    return this.showExternalApiConfig;
  }

  public logLinkState(): void {
    console.group('Link Component State');
    console.log('Facility:', this.facility);
    console.log('View:', this.viewStatus, 'Edit:', this.editStatus, 'Add:', this.addLinkStatus);
    console.groupEnd();
  }

  /**
   * Main view facility function used by frame loader
   */
  private viewFacility(frameName: string): void {
    // ensure we have a fresh local copy of the icon list to inspect
    this.iconImage = JSON.parse(JSON.stringify(CONSTANT_VALUES.ICON_LIST_IMAGE || []));

    // set viewing flags (makes component read-only etc.)
    this.setFlagBeforeViewEvent(this);

    // populate this.facility
    this.setFacilityForView(frameName);

    // Load groups from activities array
    this.loadTargetGroupsFromActivities();

    if (this.iconImage && Array.isArray(this.iconImage)) {
      for (let i = 0; i < this.iconImage.length; i++) {
        const iconObj = this.iconImage[i];
        if (this.facility && this.facility.icon === iconObj.name) {
          this.viewIcon = { name: iconObj.name, iconClass: iconObj.iconClass };
          break;
        }
      }
    }

    if (this.facility) {
      try {
        this.sidemenuSubCategory.fire({
          category: this.facility.category,
          menu: 'Links'
        });
      } catch (e) {
        // ignore if sidemenu broadcaster isn't wired in some contexts
      }

      // keep canonical icon list
      this.iconImage = CONSTANT_VALUES.ICON_LIST_IMAGE || [];

      // set default mirrors (GST etc.)
      this.setDefaultValues(this);

      // set timings & toggles for view
      try { this.setTimingsForEvent(this); } catch (e) { /* ignore */ }
      try { this.setToggleForViewFacility(); } catch (e) { /* ignore */ }

      // validate & initialize calendar date objects
      try { this.safeValidateFacilityObj(); } catch (e) { /* ignore */ }
      try { this.initializeDateForCalendar(); } catch (e) { /* ignore */ }
    } else {
      try { this.loaderEvent.fire(false); } catch (e) { /* ignore */ }
      this.router.navigate(['/links']);
    }
  }

  /**
   * Small helper: if template expects setToggleForViewFacility
   */
  private setToggleForViewFacility(): void {
    try {
      if (!this.facility) return;
      const tiers = ['member', 'nonMember', 'guest', 'corpMember', 'corpNonMember'];
      for (let i = 0; i < tiers.length; i++) {
        const t = tiers[i];
        const p = (this.facility.activityCharges as any)[t];
        this.setToggleState(t, !!(p && p.enabled));
        if (['member', 'nonMember', 'guest'].indexOf(t) !== -1) {
          this.setChildToggleState(t, !!(p && p.childEnabled));
        }
      }
      this.toggle(this, 17, !!this.facility.isGSTApplicable);
      this.toggle(this, 1, !!this.facility.isAttendeeCountRequired);
      this.toggle(this, 2, !!this.facility.isWaitlistEnabled);
      this.toggle(this, 10, !!this.facility.isAllowForceBooking);
      this.toggle(this, 15, !!this.facility.isCreatedForGroup);
    } catch (e) { /* ignore */ }
  }

  /**
   * Helper to split timings for template inputs
   */
  public setTimingsForEvent(componentContext?: any): void {
    try {
      const cmp = componentContext ? componentContext : this;
      const open = (cmp.facility && cmp.facility.timings && cmp.facility.timings.openTime) ? cmp.facility.timings.openTime : '01:00';
      const close = (cmp.facility && cmp.facility.timings && cmp.facility.timings.closeTime) ? cmp.facility.timings.closeTime : '23:00';
      const openParts = (open + '').split(':');
      const closeParts = (close + '').split(':');
      this.openTime_hour = (openParts[0] || '01').slice(0, 2);
      this.openTime_minute = (openParts[1] || '00').slice(0, 2);
      this.closeTime_hour = (closeParts[0] || '23').slice(0, 2);
      this.closeTime_minute = (closeParts[1] || '00').slice(0, 2);
    } catch (e) { /* ignore */ }
  }

  /**
 * Scan this.groupList and populate facility.targetGroupIds with groups that contain this link
 */
// ...existing code...

  /**
   * Single clear function for all test connection results
   */
  public clearTestConnectionResults(): void {
    this.testConnectionResult = '';
    this.testConnectionError = '';
  }

  /**
   * Unified test connection for all external API providers
   */
  public testExternalApiConnection(): void {
    if (!this.facility.externalApi || !this.facility.externalApi.provider) {
      this.testConnectionError = 'Please select a provider';
      this.testConnectionResult = '';
      return;
    }

    const provider = this.facility.externalApi.provider.toLowerCase();

    // Clear previous results
    this.clearTestConnectionResults();
    this.isTestingConnection = true;

    // Route to appropriate provider test
    if (provider === 'shopify') {
      this.testShopifyConnectionInternal();
    } else if (provider === 'digirestro') {
      this.testDigirestroConnectionInternal();
    } else if (provider === 'custom') {
      this.testCustomApiConnectionInternal();
    } else {
      this.isTestingConnection = false;
      this.testConnectionError = 'Unsupported provider';
    }
  }

  /**
   * Internal Digirestro connection test
   */
  private testDigirestroConnectionInternal(): void {
    const config = this.facility.externalApi.digirestro;

    // Validate required fields
    if (!config.baseUrl || config.baseUrl.trim() === '') {
      this.isTestingConnection = false;
      this.testConnectionError = 'Please enter Base URL';
      return;
    }

    if (!config.apiKey || config.apiKey.trim() === '') {
      this.isTestingConnection = false;
      this.testConnectionError = 'Please enter API Key';
      return;
    }

    if (!config.storeId || Number(config.storeId) <= 0) {
      this.isTestingConnection = false;
      this.testConnectionError = 'Please enter a valid Store ID';
      return;
    }

    // Prepare config object
    const digirestroConfig = {
      baseUrl: config.baseUrl.trim(),
      apiKey: config.apiKey.trim(),
      storeId: Number(config.storeId),
      browserUrl: config.browserUrl ? config.browserUrl.trim() : '',
      integrationId: config.integrationId ? Number(config.integrationId) : 0
    };

    // Call the unified service method
    console.log('[DEBUG] Calling testExternalApiConnection with config:', digirestroConfig);
    this.linkService.testExternalApiConnection('digirestro', digirestroConfig)
      .then((result: any) => {
        console.log('[DEBUG] Promise resolved, raw result:', result);
        console.log('[DEBUG] result.success:', result && result.success);
        console.log('[DEBUG] result.message:', result && result.message);

        this.isTestingConnection = false;

        if (result && result.success) {
          console.log('[DEBUG] SUCCESS BRANCH - Setting testConnectionResult');
          this.testConnectionResult = result.message || '✓ Connection successful!';
          this.testConnectionError = '';
          console.log('[DEBUG] testConnectionResult set to:', this.testConnectionResult);
          console.log('[DEBUG] testConnectionError cleared:', this.testConnectionError);
        } else {
          console.log('[DEBUG] FAILURE BRANCH - Setting testConnectionError');
          this.testConnectionError = result && result.message
            ? result.message
            : 'Connection failed. Please verify your credentials.';
          this.testConnectionResult = '';
          console.log('[DEBUG] testConnectionError set to:', this.testConnectionError);
          console.log('[DEBUG] testConnectionResult cleared:', this.testConnectionResult);
        }
      })
      .catch((error: any) => {
        console.log('[DEBUG] Promise rejected, error:', error);
        console.log('[DEBUG] error.message:', error && error.message);

        this.isTestingConnection = false;
        this.testConnectionError = error && error.message
          ? error.message
          : 'Connection failed. Please verify your credentials and network connection.';
        this.testConnectionResult = '';

        console.log('[DEBUG] CATCH BRANCH - testConnectionError set to:', this.testConnectionError);
        console.log('[DEBUG] CATCH BRANCH - testConnectionResult cleared:', this.testConnectionResult);
        console.error('Digirestro connection test error:', error);
      });
  }

  /**
   * Test Shopify connection (placeholder)
   */
  private testShopifyConnectionInternal(): void {
    const shopify = this.facility.externalApi.shopify;

    if (!shopify || !shopify.shopName || shopify.shopName.trim() === '') {
      this.isTestingConnection = false;
      this.testConnectionError = 'Please enter Shopify Shop Name';
      return;
    }

    // Placeholder - implement Shopify test logic
    setTimeout(() => {
      this.isTestingConnection = false;
      this.testConnectionResult = '✓ Shopify connection test (placeholder)';
    }, 1000);
  }

  /**
   * Test Custom API connection (placeholder)
   */
  private testCustomApiConnectionInternal(): void {
    const custom = this.facility.externalApi.custom;

    if (!custom || !custom.url || custom.url.trim() === '') {
      this.isTestingConnection = false;
      this.testConnectionError = 'Please enter Custom API endpoint URL';
      return;
    }

    // Placeholder - implement Custom API test logic
    setTimeout(() => {
      this.isTestingConnection = false;
      this.testConnectionResult = '✓ Custom API connection test (placeholder)';
    }, 1000);
  }
}



// WEBPACK FOOTER //
// ./src/app/facility/link/link.component.ts